import { useEffect, useMemo, useRef, useState, createContext, useContext } from "react";
import { BrowserRouter, NavLink, Route, Routes, useLocation, useNavigate, Navigate } from "react-router-dom";
import axios from "axios";
import { AreaChart, Area, ResponsiveContainer, Tooltip, XAxis, YAxis, LineChart, Line, Legend, CartesianGrid, ReferenceDot } from "recharts";
import {
  LayoutDashboard, ArrowLeftRight, Landmark, UsersRound, CalendarClock, BarChart3,
  Settings2, Bell, Search, Plus, Upload, ChevronDown, AlertTriangle, CheckCircle2,
  ArrowUpRight, ArrowDownRight, FileText, LogOut, ShieldCheck, TrendingUp, Wallet,
  RefreshCw, X, Lock, PiggyBank, Download, Clock, Trash2, Shield, Receipt, FileUp,
  Check, Loader2, Paperclip, CreditCard, Zap, CalendarDays, Truck, Building2, Sun, Moon,
} from "lucide-react";
import "@/App.css";
import CariPage from "./pages/CariPage";
import BankAccountsPage from "./pages/BankAccountsPage";
import OperationsPage from "./pages/OperationsPage";
import CreditCardsPage from "./pages/CreditCardsPage";
import DailyExpensesPage from "./pages/DailyExpensesPage";
import AuditLogPage from "./pages/AuditLogPage";
import CompaniesPage from "./pages/CompaniesPage";

axios.defaults.withCredentials = true;
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const money = (n, cur = "TRY") =>
  new Intl.NumberFormat("tr-TR", { style: "currency", currency: cur, maximumFractionDigits: 0 }).format(n || 0);
const fmtDate = (d) => (d ? d.slice(5).replace("-", ".") : "");

// ---------- AUTH ----------
const AuthCtx = createContext(null);
const useAuth = () => useContext(AuthCtx);

function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = loading, false = guest
  useEffect(() => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (window.location.hash?.includes('session_id=')) {
      return;
    }
    axios.get(`${API}/auth/me`).then(r => setUser(r.data)).catch(() => setUser(false));
  }, []);
  const login = async (email, password) => {
    const { data } = await axios.post(`${API}/auth/login`, { email, password });
    if (data.token) axios.defaults.headers.common.Authorization = `Bearer ${data.token}`;
    setUser(data);
    return data;
  };
  const logout = async () => {
    try { await axios.post(`${API}/auth/logout`); } catch {}
    delete axios.defaults.headers.common.Authorization;
    setUser(false);
  };
  return <AuthCtx.Provider value={{ user, setUser, login, logout }}>{children}</AuthCtx.Provider>;
}

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
function AuthCallback() {
  const { setUser } = useAuth();
  const nav = useNavigate();
  const location = useLocation();
  const hasProcessed = useRef(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;
    const hash = location.hash || "";
    const m = hash.match(/session_id=([^&]+)/);
    const sid = m ? decodeURIComponent(m[1]) : null;
    if (!sid) { nav("/login", { replace: true }); return; }
    (async () => {
      try {
        const { data } = await axios.post(`${API}/auth/session`, { session_id: sid });
        // Clear hash to prevent re-processing
        window.history.replaceState({}, document.title, window.location.pathname);
        if (data.status === "pending") {
          setUser(data);
          nav("/pending", { replace: true, state: { user: data } });
        } else {
          setUser(data);
          nav("/", { replace: true, state: { user: data } });
        }
      } catch (ex) {
        setErr(ex.response?.data?.detail || "Google oturum doğrulanamadı");
      }
    })();
  }, []);  // eslint-disable-line

  return <div className="auth-shell">
    <div className="auth-decor" />
    <div className="auth-card" style={{ textAlign: "center" }}>
      <div className="auth-brand"><div className="brand-mark">M</div><div><strong>mesfin</strong><span>enterprise finance</span></div></div>
      <h2>Giriş yapılıyor…</h2>
      {!err && <p className="muted">Google oturumu doğrulanıyor, lütfen bekleyin.</p>}
      {err && <>
        <div className="auth-err" data-testid="google-callback-error">{err}</div>
        <button className="outline-btn" style={{ marginTop: 12 }} onClick={() => nav("/login", { replace: true })}>Giriş sayfasına dön</button>
      </>}
    </div>
  </div>;
}

function PendingScreen() {
  const { user, logout } = useAuth();
  return <div className="auth-shell">
    <div className="auth-decor" />
    <div className="auth-card" style={{ textAlign: "center" }} data-testid="pending-screen">
      <div className="auth-brand"><div className="brand-mark">M</div><div><strong>mesfin</strong><span>enterprise finance</span></div></div>
      <h2>Hesabınız onay bekliyor</h2>
      <p className="muted">Hoş geldin{user?.name ? `, ${user.name}` : ""}! Bir admin hesabınızı onayladıktan sonra giriş yapabileceksiniz.</p>
      <div style={{ marginTop: 16 }}>
        <button className="outline-btn" onClick={logout} data-testid="pending-logout">Çıkış yap</button>
      </div>
    </div>
  </div>;
}

function LoginPage() {
  const { user, login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("admin@mesfin.com");
  const [password, setPassword] = useState("admin123");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (user && user.email) {
      if (user.status === "pending") nav("/pending", { replace: true });
      else nav("/");
    }
  }, [user, nav]);
  const submit = async (e) => {
    e.preventDefault(); setBusy(true); setErr("");
    try { await login(email, password); nav("/"); }
    catch (ex) { setErr(ex.response?.data?.detail || "Giriş başarısız"); }
    finally { setBusy(false); }
  };
  const googleSignIn = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };
  return <div className="auth-shell">
    <div className="auth-decor" />
    <form className="auth-card" onSubmit={submit}>
      <div className="auth-brand"><div className="brand-mark">M</div><div><strong>mesfin</strong><span>enterprise finance</span></div></div>
      <h2>Oturum aç</h2>
      <p className="muted">Finansınızı güvenle yönetin.</p>
      <button type="button" data-testid="google-signin-btn" onClick={googleSignIn} className="google-btn">
        <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true"><path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.9 29.3 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.9 1.2 8.1 3.1l5.7-5.7C34.1 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20c11 0 20-8 20-20 0-1.3-.1-2.3-.4-3.5z"/><path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 16.1 19 13 24 13c3.1 0 5.9 1.2 8.1 3.1l5.7-5.7C34.1 6.1 29.3 4 24 4 16.3 4 9.7 8.4 6.3 14.7z"/><path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35.2 26.7 36 24 36c-5.3 0-9.7-3.1-11.3-7.5l-6.5 5C9.6 39.6 16.3 44 24 44z"/><path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.3-2.3 4.2-4.2 5.5l6.2 5.2C40.9 35.8 44 30.3 44 24c0-1.3-.1-2.3-.4-3.5z"/></svg>
        Google ile devam et
      </button>
      <div className="auth-divider"><span>veya e-posta ile</span></div>
      <label>E-posta</label>
      <input data-testid="login-email" type="email" value={email} onChange={e => setEmail(e.target.value)} required />
      <label>Şifre</label>
      <input data-testid="login-password" type="password" value={password} onChange={e => setPassword(e.target.value)} required />
      {err && <div className="auth-err" data-testid="login-error">{String(err)}</div>}
      <button data-testid="login-submit" className="primary-btn" type="submit" disabled={busy}>{busy ? "Giriş yapılıyor…" : "Giriş yap"}</button>
      <div className="auth-hint">
        <strong>Demo erişim</strong>
        <code>admin@mesfin.com / admin123</code>
        <code>finans@mesfin.com / finans123</code>
      </div>
    </form>
  </div>;
}

function ProtectedRoute({ children }) {
  const { user } = useAuth();
  if (user === null) return <div className="loading-screen">Yükleniyor…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (user.status === "pending") return <Navigate to="/pending" replace />;
  return children;
}

function ChangePasswordModal({ onClose }) {
  const [cur, setCur] = useState("");
  const [pwd, setPwd] = useState("");
  const [pwd2, setPwd2] = useState("");
  const [err, setErr] = useState(""); const [busy, setBusy] = useState(false); const [ok, setOk] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setErr(""); setOk(false);
    if (pwd !== pwd2) { setErr("Yeni şifreler eşleşmiyor"); return; }
    if (pwd.length < 8) { setErr("Yeni şifre en az 8 karakter olmalı"); return; }
    setBusy(true);
    try {
      await axios.post(`${API}/auth/change-password`, { current_password: cur, new_password: pwd });
      setOk(true);
      setTimeout(onClose, 1200);
    } catch (ex) { setErr(ex.response?.data?.detail || "Şifre değiştirilemedi"); }
    finally { setBusy(false); }
  };
  return <div className="modal-backdrop" onClick={onClose} data-testid="change-pwd-backdrop">
    <div className="modal" onClick={e => e.stopPropagation()}>
      <div className="modal-head"><h3>Şifre değiştir</h3><button onClick={onClose} data-testid="change-pwd-close"><X size={18} /></button></div>
      <form onSubmit={submit} className="form" style={{ padding: 20 }}>
        <label>Mevcut şifre</label>
        <input data-testid="change-pwd-current" type="password" value={cur} onChange={e => setCur(e.target.value)} required />
        <label>Yeni şifre (en az 8 karakter)</label>
        <input data-testid="change-pwd-new" type="password" value={pwd} onChange={e => setPwd(e.target.value)} minLength={8} required />
        <label>Yeni şifre (tekrar)</label>
        <input data-testid="change-pwd-new2" type="password" value={pwd2} onChange={e => setPwd2(e.target.value)} minLength={8} required />
        {err && <div className="auth-err" data-testid="change-pwd-err">{err}</div>}
        {ok && <div style={{ color: "#10b981", fontSize: 13, marginTop: 10 }} data-testid="change-pwd-ok">Şifreniz güncellendi.</div>}
        <div className="form-actions">
          <button type="button" className="outline-btn" onClick={onClose}>Vazgeç</button>
          <button className="primary-btn" disabled={busy} data-testid="change-pwd-submit">{busy ? "..." : "Kaydet"}</button>
        </div>
      </form>
    </div>
  </div>;
}

// ---------- NAV ----------
// --- Theme hook ---
function useTheme() {
  const [theme, setTheme] = useState(() => localStorage.getItem("mesfin-theme") || "dark");
  useEffect(() => {
    document.body.dataset.theme = theme;
    localStorage.setItem("mesfin-theme", theme);
  }, [theme]);
  return [theme, setTheme];
}

function ThemeToggle() {
  const [theme, setTheme] = useTheme();
  return <button
    className="icon-btn"
    data-testid="theme-toggle"
    onClick={() => setTheme(theme === "dark" ? "light" : "dark")}
    title={theme === "dark" ? "Açık temaya geç" : "Koyu temaya geç"}>
    {theme === "dark" ? <Sun size={18} /> : <Moon size={18} />}
  </button>;
}

// --- Quick Search dropdown ---
function QuickSearch() {
  const nav = useNavigate();
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [data, setData] = useState({ groups: [], total: 0 });
  const [loading, setLoading] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h); return () => document.removeEventListener("mousedown", h);
  }, []);
  useEffect(() => {
    if (q.trim().length < 2) { setData({ groups: [], total: 0 }); return; }
    const t = setTimeout(async () => {
      setLoading(true);
      try {
        const { data: r } = await axios.get(`${API}/search`, { params: { q, limit: 6 } });
        setData(r); setOpen(true);
      } finally { setLoading(false); }
    }, 220);
    return () => clearTimeout(t);
  }, [q]);
  const go = (link) => { setOpen(false); setQ(""); nav(link); };
  return <div ref={ref} className="search-wrap" data-testid="quick-search-wrap">
    <Search size={16} />
    <input
      type="text" value={q} onChange={e => setQ(e.target.value)} onFocus={() => q.length >= 2 && setOpen(true)}
      placeholder="Ara: cari, çek, kredi, kart..."
      data-testid="quick-search-input"
      className="search-input"
    />
    {open && (data.total > 0 || loading) && <div className="search-dropdown" data-testid="search-dropdown">
      {loading && <div className="search-empty">Aranıyor…</div>}
      {!loading && data.groups.length === 0 && <div className="search-empty">Sonuç yok</div>}
      {!loading && data.groups.map(g => <div key={g.label} className="search-group">
        <div className="search-group-head" data-testid={`search-group-${g.label}`}>{g.label} <span>{g.items.length}</span></div>
        {g.items.map(it => <button key={it.id} className="search-item" data-testid={`search-item-${it.id}`} onClick={() => go(g.link)}>
          <strong>{it.title}</strong>
          <span>{it.subtitle}</span>
        </button>)}
      </div>)}
    </div>}
  </div>;
}

// --- Notification Bell ---
function NotificationBell() {
  const nav = useNavigate();
  const [data, setData] = useState({ items: [], unread: 0 });
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const load = () => axios.get(`${API}/notifications`).then(r => setData(r.data)).catch(() => {});
  useEffect(() => {
    load();
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    const timer = setInterval(load, 60000);
    return () => { document.removeEventListener("mousedown", h); clearInterval(timer); };
  }, []);
  const toggle = async () => {
    const willOpen = !open;
    setOpen(willOpen);
    if (willOpen && data.unread > 0) {
      try { await axios.post(`${API}/notifications/mark-read`); } catch {}
      setData(d => ({ ...d, unread: 0 }));
    }
  };
  const sevColor = { high: "#ff8585", medium: "#ffc06b", low: "#6ba7ff" };
  const go = (link) => { setOpen(false); nav(link); };
  return <div ref={ref} className="notif-wrap">
    <button className="icon-btn notification" data-testid="notifications-button" onClick={toggle}>
      <Bell size={18} />
      {data.unread > 0 && <span className="notif-badge" data-testid="notif-badge">{data.unread > 9 ? "9+" : data.unread}</span>}
    </button>
    {open && <div className="notif-dropdown" data-testid="notif-dropdown">
      <div className="notif-head">
        <strong>Bildirimler</strong>
        <span>{data.items.length} kayıt</span>
      </div>
      {data.items.length === 0 && <div className="notif-empty">Bildirim yok 👌</div>}
      <div className="notif-list">
        {data.items.map(it => <button key={it.id} className="notif-item" data-testid={`notif-item-${it.id}`} onClick={() => go(it.link)}>
          <span className="notif-dot" style={{ background: sevColor[it.severity] || "#6ba7ff" }} />
          <div>
            <strong>{it.title}</strong>
            <span>{it.subtitle}</span>
          </div>
        </button>)}
      </div>
    </div>}
  </div>;
}

const navItems = [
  { to: "/", label: "Genel Bakış", icon: LayoutDashboard },
  { to: "/transactions", label: "Gelir & Gider", icon: ArrowLeftRight },
  { to: "/calendar", label: "Ödeme Takvimi", icon: CalendarClock },
  { to: "/cari", label: "Cari Hesaplar", icon: UsersRound },
  { to: "/bank-accounts", label: "Banka Hesapları", icon: Building2 },
  { to: "/operations", label: "Operasyon", icon: Truck },
  { to: "/checks", label: "Çekler", icon: FileText },
  { to: "/credits", label: "Krediler", icon: CreditCard },
  { to: "/credit-cards", label: "Kredi Kartları", icon: CreditCard },
  { to: "/escrows", label: "Emanet", icon: PiggyBank },
  { to: "/daily-expenses", label: "Günlük Ödemeler", icon: Receipt },
  { to: "/finance-costs", label: "Finans Giderleri", icon: Receipt },
  { to: "/rates", label: "Döviz Kurları", icon: TrendingUp },
  { to: "/reports", label: "Raporlar", icon: BarChart3 },
  { to: "/companies", label: "Şirketler", icon: Building2, adminOnly: true },
  { to: "/users", label: "Kullanıcılar", icon: Shield, adminOnly: true },
  { to: "/audit-log", label: "Denetim Geçmişi", icon: ShieldCheck, adminOnly: true },
];

// Bank logo helper (Clearbit API)
function BankLogo({ name, banks, size = 20 }) {
  const [failed, setFailed] = useState(false);
  if (!name) return null;
  const bank = banks?.find(b => b.name === name || b.id === name);
  if (bank?.domain && !failed) {
    return <img
      src={`https://logo.clearbit.com/${bank.domain}`}
      alt={bank.name} className="bank-logo"
      style={{ width: size, height: size }}
      onError={(e) => {
        // Fallback to Google favicon
        if (!e.target.dataset.fallback) {
          e.target.dataset.fallback = "1";
          e.target.src = `https://www.google.com/s2/favicons?domain=${bank.domain}&sz=64`;
        } else {
          setFailed(true);
        }
      }}
    />;
  }
  return <div className="bank-logo-fallback" style={{ width: size, height: size }}>
    <Landmark size={size * 0.6} />
  </div>;
}
function BankCell({ name, banks }) {
  if (!name) return <span className="muted">—</span>;
  return <div className="bank-cell"><BankLogo name={name} banks={banks} size={18} /><span>{name}</span></div>;
}

function Shell({ children, company, setCompany, companies }) {
  const location = useLocation();
  const { user, logout } = useAuth();
  const [menu, setMenu] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const [showChangePwd, setShowChangePwd] = useState(false);
  const title = navItems.find(x => x.to === location.pathname)?.label || "Genel Bakış";
  const roleLabel = { admin: "Yönetici", finance: "Finans", viewer: "Görüntüleyici" }[user?.role] || "";
  const allowed = user?.allowed_companies || [];
  const visibleCompanies = (user?.role === "admin" || allowed.length === 0)
    ? companies
    : companies.filter(c => allowed.includes(c.id));
  return <div className="app-shell">
    <aside className="sidebar">
      <div className="brand"><div className="brand-mark">M</div><div><strong>mesfin</strong><span>enterprise finance</span></div></div>
      <div className="workspace-label">ÇALIŞMA ALANI</div>
      <div className="company-picker">
        <button className="company-switcher" data-testid="company-switcher" onClick={() => setMenu(!menu)}>
          <span className="company-dot" />
          <span>{company === "all" ? "Tüm şirketler" : companies.find(c => c.id === company)?.name}</span>
          <ChevronDown size={15} />
        </button>
        {menu && <div className="company-menu" data-testid="company-menu">
          <button data-testid="company-option-all" onClick={() => { setCompany("all"); setMenu(false); }}>Tüm şirketler</button>
          {visibleCompanies.map(c => <button key={c.id} data-testid={`company-option-${c.id}`} onClick={() => { setCompany(c.id); setMenu(false); }}>
            <span className="company-dot" style={{ background: c.color }} />{c.name}
          </button>)}
        </div>}
      </div>
      <nav>{navItems.filter(x => !x.adminOnly || user?.role === "admin").map(({ to, label, icon: Icon }) => <NavLink
        data-testid={`nav-${label.toLowerCase().replaceAll(" ", "-").replaceAll("&", "and")}`}
        className={({ isActive }) => isActive ? "active" : ""} to={to} key={to}>
        <Icon size={18} /><span>{label}</span>
      </NavLink>)}</nav>
      <div className="sidebar-bottom">
        <div className="profile" onClick={() => setProfileOpen(!profileOpen)} data-testid="profile-menu">
          <div className="avatar">{(user?.name || user?.email || "U").slice(0, 2).toUpperCase()}</div>
          <div><strong>{user?.name || user?.email}</strong><span>{roleLabel}</span></div>
          <ChevronDown size={15} />
        </div>
        {profileOpen && <div className="profile-menu" data-testid="profile-dropdown">
          <button onClick={() => { setShowChangePwd(true); setProfileOpen(false); }} data-testid="change-pwd-btn"><Lock size={14} /> Şifre değiştir</button>
          <button onClick={logout} data-testid="logout-btn"><LogOut size={14} /> Çıkış yap</button>
        </div>}
      </div>
    </aside>
    {showChangePwd && <ChangePasswordModal onClose={() => setShowChangePwd(false)} />}
    <main className="main">
      <header>
        <div><div className="breadcrumb">MESFIN <span>/</span> {title.toUpperCase()}</div><h1>{title}</h1></div>
        <div className="header-actions">
          <QuickSearch />
          <ThemeToggle />
          <NotificationBell />
          <span className="role-chip" data-testid="role-chip"><ShieldCheck size={13} /> {roleLabel}</span>
        </div>
      </header>
      {children}
    </main>
  </div>;
}

// ---------- DASHBOARD ----------
function Dashboard({ data, company }) {
  const [projection, setProjection] = useState(null);
  const [planDate, setPlanDate] = useState(new Date().toISOString().slice(0, 10));
  const [downloading, setDownloading] = useState(false);
  useEffect(() => {
    axios.get(`${API}/cash-projection`, { params: { days: 90, company_id: company } }).then(r => setProjection(r.data));
  }, [company]);
  const downloadPlan = async () => {
    setDownloading(true);
    try {
      const res = await axios.get(`${API}/daily-payment-plan/export`, { params: { date: planDate }, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `odeme-plani-${planDate}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (ex) {
      alert(ex.response?.data?.detail || "Ödeme planı indirilemedi, lütfen tekrar deneyin.");
    } finally { setDownloading(false); }
  };
  return <div className="page">
    <section className="welcome">
      <div>
        <p className="eyebrow">{new Date().toLocaleDateString("tr-TR", { weekday: "long", day: "numeric", month: "long", year: "numeric" }).toUpperCase()}</p>
        <h2>Günaydın.</h2>
        <p className="muted">Finansal görünümünüz kontrol altında. <b>{data.obligations.filter(o => o.status === "Gecikmiş").length} gecikmiş</b> kalem var.</p>
      </div>
      <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
        <input type="date" value={planDate} onChange={e => setPlanDate(e.target.value)} data-testid="plan-date-input"
          style={{ padding: "9px 12px", borderRadius: 6, border: "1px solid var(--input-border)", background: "var(--input-bg)", color: "var(--text)", fontSize: 13 }} />
        <button className="primary-btn" data-testid="download-plan-btn" onClick={downloadPlan} disabled={downloading}>
          <Download size={16} /> {downloading ? "Hazırlanıyor..." : "Günlük ödeme planını indir"}
        </button>
        <NavLink to="/calendar" className="outline-btn" data-testid="go-calendar-btn"><CalendarClock size={16} /> Ödeme Takvimi</NavLink>
      </div>
    </section>
    <section className="metric-grid">
      <Metric label="Toplam Nakit" value={money(data.cash_balance)} change="%8,4" trend="up" icon="₺" />
      <Metric label="Bu Dönem Gelir" value={money(data.income)} change="%12,8" trend="up" icon="↗" />
      <Metric label="Bu Dönem Gider" value={money(data.expense)} change="%3,2" trend="down" icon="↘" />
      <Metric label="Bekleyen Ödeme" value={money(data.due_amount)} change={`${data.obligations.length} kayıt`} trend="warn" icon="!" />
    </section>

    {projection && projection.series.length > 0 && (
      <section className="panel cash-projection" data-testid="cash-projection">
        <div className="panel-heading">
          <div><h3>90 günlük nakit projeksiyon</h3>
            <p>Krediler + çekler + takvim · min bakiye <b className={projection.min_balance < 0 ? "late-text" : "income"}>{money(projection.min_balance)}</b> {projection.min_balance_date}</p>
          </div>
          <div className="chart-legend">
            <span><i style={{ background: "#4f91ff" }} /> Bakiye</span>
            <span><i style={{ background: "#22c993" }} /> Giriş</span>
            <span><i style={{ background: "#f06c6c" }} /> Çıkış</span>
          </div>
        </div>
        <div style={{ height: 280, padding: "20px 20px 10px" }}>
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={projection.series.filter((_, i) => i % 3 === 0 || i === projection.series.length - 1)} margin={{ top: 10, right: 20, left: 10, bottom: 5 }}>
              <defs><linearGradient id="bal" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#4f91ff" stopOpacity={.35} /><stop offset="100%" stopColor="#4f91ff" stopOpacity={0} /></linearGradient></defs>
              <CartesianGrid stroke="#22344a" strokeDasharray="3 3" />
              <XAxis dataKey="date" tick={{ fill: "#8b98aa", fontSize: 10 }} axisLine={false} tickLine={false} tickFormatter={d => d.slice(5)} />
              <YAxis tick={{ fill: "#8b98aa", fontSize: 10 }} axisLine={false} tickLine={false} tickFormatter={v => Intl.NumberFormat("tr-TR", { notation: "compact" }).format(v)} />
              <Tooltip contentStyle={{ background: "#172131", border: "1px solid #34445b", borderRadius: 8, color: "#fff", fontSize: 11 }} formatter={(v) => money(v)} />
              <Area type="monotone" dataKey="balance" stroke="#4f91ff" fill="url(#bal)" strokeWidth={2.5} name="Bakiye" />
              <Line type="monotone" dataKey="outflow" stroke="#f06c6c" strokeWidth={1.5} dot={false} name="Çıkış" />
              <Line type="monotone" dataKey="inflow" stroke="#22c993" strokeWidth={1.5} dot={false} name="Giriş" />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </section>
    )}

    <div className="dashboard-grid">
      <section className="panel obligations full-width-obligations">
        <div className="panel-heading"><div><h3>Yaklaşan ödemeler</h3><p>Önceliklendirilmiş liste</p></div>
          <NavLink to="/calendar" className="text-link" data-testid="view-calendar-link">Tümünü gör →</NavLink>
        </div>
        {data.obligations.slice(0, 5).map((x, i) => <Obligation key={x.id} item={x} index={i} />)}
      </section>
    </div>
    <section className="panel transactions">
      <div className="panel-heading"><div><h3>Son hareketler</h3><p>{company === "all" ? "Tüm şirketler" : "Seçili şirket"} · Gerçek zamanlı</p></div>
        <NavLink to="/transactions" className="text-link" data-testid="view-transactions-link">Hareketleri gör →</NavLink>
      </div>
      <TransactionTable rows={data.transactions.slice(0, 6)} />
    </section>
  </div>;
}

function Metric({ label, value, change, trend, icon }) {
  return <div className="metric" data-testid={`metric-${label.toLowerCase().replaceAll(" ", "-")}`}>
    <div className={`metric-icon ${trend}`}>{icon}</div>
    <p>{label}</p><strong>{value}</strong>
    <span className={`trend ${trend}`}>{trend === "up" ? <ArrowUpRight size={13} /> : trend === "down" ? <ArrowDownRight size={13} /> : <AlertTriangle size={13} />} {change}</span>
    <small>{trend === "warn" ? "dikkat gerekiyor" : "geçen döneme göre"}</small>
  </div>;
}

function Obligation({ item, index }) {
  const late = item.status === "Gecikmiş";
  return <div className="obligation" data-testid={`obligation-${index}`}>
    <div className={`ob-icon ${late ? "late" : ""}`}>{late ? <AlertTriangle size={17} /> : <CalendarClock size={17} />}</div>
    <div className="ob-main"><strong>{item.title}</strong><span>{item.counterparty} · {item.kind}</span></div>
    <div className="ob-amount">
      <strong>{money(item.amount, item.currency)}</strong>
      {item.currency !== "TRY" && <small className="try-eq">≈ {money(item.try_amount)}</small>}
      <span className={late ? "late-text" : ""}>{late ? `${item.overdue_days} gün gecikti` : fmtDate(item.due_date)}</span>
    </div>
  </div>;
}

function TransactionTable({ rows }) {
  return <div className="table-scroll"><table>
    <thead><tr><th>AÇIKLAMA</th><th>ŞİRKET</th><th>TÜR</th><th>TUTAR</th><th>TL EŞ.</th><th>TARİH</th></tr></thead>
    <tbody>{rows.map(x => <tr key={x.id} data-testid={`transaction-row-${x.id}`}>
      <td><strong>{x.description}</strong><span>{x.account} · {x.category}</span></td>
      <td><span className="company-pill">{x.company_id.toUpperCase()}</span></td>
      <td><span className={`type ${x.type}`}>{x.type === "income" ? "Gelir" : "Gider"}</span></td>
      <td className={x.type === "income" ? "income" : "expense"}>{x.type === "income" ? "+" : "−"}{money(x.amount, x.currency)}</td>
      <td>{money(x.try_amount)}</td>
      <td>{x.transaction_date}</td>
    </tr>)}</tbody>
  </table></div>;
}

// ---------- TRANSACTIONS PAGE ----------
function TransactionsPage({ company, data, reload }) {
  const { user } = useAuth();
  const canEdit = user?.role !== "viewer";
  const [showForm, setShowForm] = useState(false);
  const importData = async (event) => {
    const file = event.target.files?.[0]; if (!file) return;
    const form = new FormData(); form.append("file", file);
    await axios.post(`${API}/import`, form, { params: { company_id: (company === "all" ? (companies[0]?.id || "") : company) } });
    reload();
  };
  return <div className="page">
    <section className="page-toolbar">
      <div><p className="eyebrow">GELİR & GİDER</p><h2>Tüm Hareketler</h2><p className="muted">Finans ekibi için güncel kayıt görünümü.</p></div>
      {canEdit && <div className="toolbar-actions">
        <label className="outline-btn" data-testid="import-data-button"><Upload size={16} /> CSV / Excel aktar
          <input data-testid="import-file-input" type="file" accept=".csv,.xlsx,.xls" onChange={importData} hidden />
        </label>
        <button className="primary-btn" data-testid="add-record-button" onClick={() => setShowForm(true)}><Plus size={17} /> Yeni kayıt</button>
      </div>}
    </section>
    <section className="panel full-panel"><TransactionTable rows={data.transactions} /></section>
    {showForm && <TransactionForm company={company} onClose={() => setShowForm(false)} onDone={() => { setShowForm(false); reload(); }} />}
  </div>;
}

function TransactionForm({ company, onClose, onDone }) {
  const [form, setForm] = useState({
    company_id: (company === "all" ? (companies[0]?.id || "") : company), type: "expense", category: "Genel",
    description: "", amount: "", currency: "TRY", manual_rate: "", account: "Merkez Banka",
    transaction_date: new Date().toISOString().slice(0, 10),
  });
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      const payload = { ...form, amount: parseFloat(form.amount) };
      if (form.manual_rate) payload.manual_rate = parseFloat(form.manual_rate);
      else delete payload.manual_rate;
      await axios.post(`${API}/transactions`, payload);
      onDone();
    } catch (ex) { setErr(ex.response?.data?.detail || "Kayıt eklenemedi"); }
    finally { setBusy(false); }
  };
  return <Modal onClose={onClose} title="Yeni gelir / gider">
    <form onSubmit={submit} className="form">
      <div className="row2">
        <Field label="Tür"><select data-testid="tx-type" value={form.type} onChange={e => setForm({ ...form, type: e.target.value })}><option value="income">Gelir</option><option value="expense">Gider</option></select></Field>
        <Field label="Kategori"><input data-testid="tx-category" value={form.category} onChange={e => setForm({ ...form, category: e.target.value })} required /></Field>
      </div>
      <Field label="Açıklama"><input data-testid="tx-description" value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} required /></Field>
      <div className="row2">
        <Field label="Tutar"><input data-testid="tx-amount" type="number" step="0.01" value={form.amount} onChange={e => setForm({ ...form, amount: e.target.value })} required /></Field>
        <Field label="Para birimi"><select data-testid="tx-currency" value={form.currency} onChange={e => setForm({ ...form, currency: e.target.value })}>{["TRY", "USD", "EUR", "GBP"].map(c => <option key={c}>{c}</option>)}</select></Field>
      </div>
      {form.currency !== "TRY" && <Field label="Manuel kur (opsiyonel, boş: TCMB)"><input data-testid="tx-manual-rate" type="number" step="0.0001" value={form.manual_rate} onChange={e => setForm({ ...form, manual_rate: e.target.value })} placeholder="örn 32.45" /></Field>}
      <div className="row2">
        <Field label="Hesap"><input data-testid="tx-account" value={form.account} onChange={e => setForm({ ...form, account: e.target.value })} /></Field>
        <Field label="Tarih"><input data-testid="tx-date" type="date" value={form.transaction_date} onChange={e => setForm({ ...form, transaction_date: e.target.value })} required /></Field>
      </div>
      {err && <div className="auth-err">{String(err)}</div>}
      <div className="form-actions"><button type="button" className="outline-btn" onClick={onClose}>Vazgeç</button><button data-testid="tx-submit" className="primary-btn" disabled={busy}>{busy ? "Kaydediliyor…" : "Kaydet"}</button></div>
    </form>
  </Modal>;
}

// ---------- CALENDAR PAGE ----------
function CalendarPage({ company, companies }) {
  const { user } = useAuth();
  const canEdit = user?.role !== "viewer";
  const [period, setPeriod] = useState("month");
  const [kind, setKind] = useState("all");
  const [custom, setCustom] = useState({ start: "", end: "" });
  const [data, setData] = useState(null);
  const [overdue, setOverdue] = useState([]);
  const [overdueOpen, setOverdueOpen] = useState(true);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [showForm, setShowForm] = useState(false);
  const [meta, setMeta] = useState({ payment_kinds: [], currencies: [], banks: [] });
  useEffect(() => { axios.get(`${API}/meta`).then(r => setMeta(r.data)); }, []);
  const load = () => {
    const params = { period, company_id: company, kind };
    if (period === "custom") { params.start = custom.start; params.end = custom.end; if (!params.start || !params.end) return; }
    axios.get(`${API}/calendar/payments`, { params }).then(r => setData(r.data));
    const obParams = { company_id: company };
    if (kind && kind !== "all") obParams.kind = kind;
    axios.get(`${API}/obligations`, { params: obParams }).then(r =>
      setOverdue(r.data.filter(o => o.status === "Gecikmiş" && !o.installment_plan_id))
    );
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [period, company, kind, custom]);
  const exportFile = async (fmt) => {
    const params = new URLSearchParams({ period, format: fmt });
    if (company && company !== "all") params.append("company_id", company);
    if (kind && kind !== "all") params.append("kind", kind);
    if (period === "custom") { params.append("start", custom.start); params.append("end", custom.end); }
    const resp = await axios.get(`${API}/calendar/export?${params}`, { responseType: "blob" });
    const url = URL.createObjectURL(resp.data);
    const a = document.createElement("a");
    a.href = url;
    a.download = `odeme_takvimi.${fmt}`;
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
  };
  const periodTabs = [["day", "Günlük"], ["week", "Haftalık"], ["month", "Aylık"], ["year", "Yıllık"], ["custom", "Özel"]];
  const overdueTotalTry = overdue.reduce((a, o) => a + (o.try_amount || 0), 0);

  const toggleSelect = (id) => {
    const next = new Set(selectedIds);
    if (next.has(id)) next.delete(id); else next.add(id);
    setSelectedIds(next);
  };
  const clearSelection = () => setSelectedIds(new Set());
  const bulkAction = async (action, extra = {}) => {
    if (selectedIds.size === 0) return;
    await axios.post(`${API}/obligations/bulk`, { ids: [...selectedIds], action, ...extra });
    clearSelection(); load();
  };
  const bulkShift = () => {
    const days = window.prompt("Kaç gün kaydırılsın? (negatif değer öncesine kaydırır)", "7");
    if (!days) return;
    const n = parseInt(days);
    if (isNaN(n) || n === 0) return;
    bulkAction("shift", { shift_days: n });
  };
  const renderRow = (x) => {
    const c = companies.find(cc => cc.id === x.company_id);
    const markPaid = async () => { await axios.post(`${API}/obligations/${x.id}/pay`); load(); };
    const selectable = canEdit && x.status !== "Ödendi";
    return <tr key={x.id} data-testid={`cal-row-${x.id}`} className={selectedIds.has(x.id) ? "row-selected" : ""}>
      <td>{selectable && <input type="checkbox" data-testid={`select-${x.id}`} checked={selectedIds.has(x.id)} onChange={() => toggleSelect(x.id)} className="row-check" />}</td>
      <td><span className="company-pill" style={{ background: `${c?.color}22`, color: c?.color }}>{c?.short || x.company_id}</span></td>
      <td><strong>{x.title}</strong><span>{x.counterparty}</span></td>
      <td><BankCell name={x.bank} banks={meta.banks} /></td>
      <td><span className="kind-pill">{x.kind}</span></td>
      <td>{fmtDate(x.due_date)}{x.status === "Gecikmiş" && <small className="late-text"> · {x.overdue_days}g</small>}</td>
      <td className={x.currency === "TRY" ? "cur-active" : ""}>{x.currency === "TRY" ? money(x.amount, "TRY") : "—"}</td>
      <td className={x.currency === "USD" ? "cur-active" : ""}>{x.currency === "USD" ? money(x.amount, "USD") : "—"}</td>
      <td className={x.currency === "EUR" ? "cur-active" : ""}>{x.currency === "EUR" ? money(x.amount, "EUR") : "—"}</td>
      <td className={x.currency === "GBP" ? "cur-active" : ""}>{x.currency === "GBP" ? money(x.amount, "GBP") : "—"}</td>
      <td><b>{money(x.try_amount)}</b></td>
      <td>{canEdit && x.status !== "Ödendi" && <button className="link-btn pay-btn" data-testid={`pay-${x.id}`} onClick={markPaid}><Check size={13} /> Ödendi</button>}
          {x.pdf_attachment_id && <a className="link-btn attach-btn" href={`${API}/attachments/${x.pdf_attachment_id}`} target="_blank" rel="noreferrer" title="Ekli PDF"><Paperclip size={13} /></a>}</td>
    </tr>;
  };
  return <div className="page">
    <section className="page-toolbar">
      <div><p className="eyebrow">ÖDEME TAKVİMİ</p><h2>Yaklaşan Ödemeler ve Tahsilatlar</h2><p className="muted">{data ? `${data.count} kayıt · ${data.start} → ${data.end}` : "Yükleniyor…"}</p></div>
      <div className="toolbar-actions">
        <button className="outline-btn" data-testid="export-xlsx-btn" onClick={() => exportFile("xlsx")}><Download size={16} /> Excel</button>
        <button className="outline-btn" data-testid="export-csv-btn" onClick={() => exportFile("csv")}><Download size={16} /> CSV</button>
        {canEdit && <button className="primary-btn" data-testid="add-obligation-btn" onClick={() => setShowForm(true)}><Plus size={17} /> Yeni ödeme</button>}
      </div>
    </section>

    <div className="cal-controls">
      <div className="period-tabs" data-testid="period-tabs">
        {periodTabs.map(([k, l]) => <button key={k} data-testid={`period-${k}`} className={period === k ? "active" : ""} onClick={() => setPeriod(k)}>{l}</button>)}
      </div>
      <div className="filters">
        <select data-testid="filter-kind" value={kind} onChange={e => setKind(e.target.value)}>
          <option value="all">Tüm ödeme tipleri</option>
          {meta.payment_kinds.map(k => <option key={k}>{k}</option>)}
        </select>
        {period === "custom" && <>
          <input data-testid="custom-start" type="date" value={custom.start} onChange={e => setCustom({ ...custom, start: e.target.value })} />
          <input data-testid="custom-end" type="date" value={custom.end} onChange={e => setCustom({ ...custom, end: e.target.value })} />
        </>}
      </div>
    </div>

    {data && <>
      <div className="totals-row">
        <div className="total-card tl"><span>TL Eşdeğeri Toplam</span><strong>{money(data.total_try)}</strong></div>
        <div className="total-card tl eur"><span>EUR eşdeğeri</span><strong>{money(data.total_eur, "EUR")}</strong></div>
        {["TRY", "USD", "EUR", "GBP"].map(c => (
          <div key={c} className="total-card"><span>{c}</span><strong>{money(data.totals_by_currency[c] || 0, c)}</strong></div>
        ))}
      </div>

      {overdue.length > 0 && (
        <section className="panel overdue-panel" data-testid="overdue-group">
          <button className="overdue-head" onClick={() => setOverdueOpen(!overdueOpen)} data-testid="overdue-toggle">
            <div className="overdue-left">
              <div className="overdue-icon"><AlertTriangle size={18} /></div>
              <div>
                <strong>Gecikmiş ödemeler</strong>
                <span>{overdue.length} kayıt · {company === "all" ? "tüm şirketler" : "seçili şirket"}</span>
              </div>
            </div>
            <div className="overdue-right">
              <strong>{money(overdueTotalTry)}</strong>
              <ChevronDown size={18} className={`chev ${overdueOpen ? "open" : ""}`} />
            </div>
          </button>
          {overdueOpen && (
            <div className="table-scroll"><table className="cal-table">
              <thead><tr><th></th><th>ŞİRKET</th><th>İSİM</th><th>BANKA</th><th>TİP</th><th>TARİH</th><th>TRY</th><th>USD</th><th>EUR</th><th>GBP</th><th>TL EŞ.</th><th></th></tr></thead>
              <tbody>{overdue.filter(o => !o.installment_plan_id).map(renderRow)}</tbody>
            </table></div>
          )}
        </section>
      )}

      <section className="panel cal-panel">
        {data.groups.length === 0 && <div className="empty">Bu aralıkta kayıt yok. (Taksit planları Krediler sekmesinde)</div>}
        {data.groups.map(g => <div className="cal-group" key={g.date} data-testid={`cal-group-${g.date}`}>
          <div className="cal-group-header">
            <div><strong>{new Date(g.date).toLocaleDateString("tr-TR", { weekday: "long", day: "numeric", month: "long" })}</strong>
              <span>{g.count} kayıt</span></div>
            <strong className="group-total">{money(g.total_try)}</strong>
          </div>
          <div className="table-scroll"><table className="cal-table">
            <thead><tr><th></th><th>ŞİRKET</th><th>İSİM</th><th>BANKA</th><th>TİP</th><th>TARİH</th><th>TRY</th><th>USD</th><th>EUR</th><th>GBP</th><th>TL EŞ.</th><th></th></tr></thead>
            <tbody>{g.items.map(renderRow)}</tbody>
          </table></div>
        </div>)}
      </section>
    </>}
    {showForm && <ObligationForm company={company} meta={meta} onClose={() => setShowForm(false)} onDone={() => { setShowForm(false); load(); }} />}
    {selectedIds.size > 0 && (
      <div className="bulk-bar" data-testid="bulk-bar">
        <div className="bulk-info">
          <strong>{selectedIds.size}</strong> kayıt seçildi
        </div>
        <div className="bulk-actions">
          <button type="button" className="outline-btn" onClick={bulkShift} data-testid="bulk-shift"><CalendarDays size={14} /> Tarih kaydır</button>
          <button type="button" className="primary-btn" onClick={() => bulkAction("pay")} data-testid="bulk-pay"><Check size={14} /> Ödendi işaretle</button>
          <button type="button" className="icon-btn-sm" onClick={clearSelection} data-testid="bulk-clear"><X size={14} /></button>
        </div>
      </div>
    )}
  </div>;
}

// ---------- EARLY CLOSURE MODAL ----------
function EarlyClosureModal({ plan, onClose, onDone }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { axios.get(`${API}/installment-plans/${plan.plan_id}/early-closure`).then(r => setData(r.data)); }, [plan.plan_id]);
  const confirm = async () => {
    setBusy(true);
    try { await axios.post(`${API}/installment-plans/${plan.plan_id}/close-early`); onDone(); }
    finally { setBusy(false); }
  };
  if (!data) return <Modal onClose={onClose} title="Erken kapatma"><div className="form"><p className="muted">Hesaplanıyor…</p></div></Modal>;
  return <Modal onClose={onClose} title="Erken kapatma simülasyonu">
    <div className="form">
      <div className="early-head">
        <Zap size={28} />
        <div>
          <strong>{plan.title}</strong>
          <span>{plan.counterparty} · {plan.bank}</span>
        </div>
      </div>
      <div className="early-grid">
        <div className="early-stat"><span>Kalan taksit</span><strong>{data.remaining_count}</strong></div>
        <div className="early-stat"><span>Ödenen</span><strong>{data.paid_count}</strong></div>
        <div className="early-stat big"><span>Bugün ödenecek anapara</span><strong>{money(data.payoff_today, data.currency)}</strong>{data.currency !== "TRY" && <small>≈ {money(data.payoff_today_try)}</small>}</div>
        <div className="early-stat savings"><span>Tasarruf (faiz + komisyon)</span><strong>{money(data.savings, data.currency)}</strong>{data.currency !== "TRY" && <small>≈ {money(data.savings_try)}</small>}</div>
      </div>
      {data.sum_remaining_principal > 0 ? <div className="early-breakdown">
        <div><span>Kalan anapara</span><b>{money(data.sum_remaining_principal, data.currency)}</b></div>
        <div><span>Kalan faiz</span><b className="interest-text">{money(data.sum_remaining_interest, data.currency)}</b></div>
        <div><span>Kalan komisyon</span><b className="interest-text">{money(data.sum_remaining_commission, data.currency)}</b></div>
      </div> : <div className="early-note muted">⚠ Bu planda anapara/faiz dağılımı bulunmadığı için tasarruf hesaplanamaz; kalan tüm taksitler bugün ödenmiş olarak işaretlenecek.</div>}
      <div className="form-actions">
        <button type="button" className="outline-btn" onClick={onClose}>Vazgeç</button>
        <button type="button" className="primary-btn" onClick={confirm} disabled={busy} data-testid="early-confirm">
          {busy ? "İşleniyor…" : `${data.remaining_count} taksiti kapat`}
        </button>
      </div>
    </div>
  </Modal>;
}

function ObligationForm({ company, meta, onClose, onDone }) {
  const [form, setForm] = useState({
    company_id: (company === "all" ? (companies[0]?.id || "") : company), kind: "Çek", title: "",
    counterparty: "", bank: "", amount: "", currency: "TRY", manual_rate: "",
    due_date: new Date(Date.now() + 7 * 86400000).toISOString().slice(0, 10), status: "Bekliyor",
  });
  const [installment, setInstallment] = useState(false);
  const [plan, setPlan] = useState({ count: 3, interval_days: 30 });
  const [pdfParsing, setPdfParsing] = useState(false);
  const [pdfResult, setPdfResult] = useState(null); // {attachment_id, filename, rows, bank}
  const [pdfBatch, setPdfBatch] = useState([]); // multiple parsed plans
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const banks = meta.banks || [];

  const handlePdfUpload = async (e) => {
    const files = Array.from(e.target.files || []); if (!files.length) return;
    setPdfParsing(true); setErr("");
    const batch = [];
    try {
      for (const file of files) {
        const fd = new FormData(); fd.append("file", file);
        const r = await axios.post(`${API}/obligations/installments/parse-pdf`, fd);
        batch.push(r.data);
      }
      if (batch.length === 1) {
        setPdfResult(batch[0]);
        if (batch[0].bank && !form.bank) {
          const matched = banks.find(b => batch[0].bank && b.name.toLowerCase().includes(batch[0].bank.toLowerCase().slice(0, 5)));
          if (matched) setForm(f => ({ ...f, bank: matched.name }));
        }
      } else {
        setPdfBatch(batch);
      }
    } catch (ex) {
      setErr(ex.response?.data?.detail || "PDF okunamadı");
    } finally { setPdfParsing(false); }
  };

  const updatePdfRow = (idx, field, val) => {
    setPdfResult(p => ({ ...p, rows: p.rows.map((r, i) => i === idx ? { ...r, [field]: val } : r) }));
  };
  const removePdfRow = (idx) => setPdfResult(p => ({ ...p, rows: p.rows.filter((_, i) => i !== idx) }));

  const submit = async (e) => {
    e.preventDefault(); setBusy(true); setErr("");
    try {
      if (installment && pdfBatch.length > 0) {
        // Batch: create one plan per PDF
        for (const b of pdfBatch) {
          if (!b.rows || b.rows.length === 0) continue;
          const payload = {
            company_id: form.company_id, kind: form.kind || "Taksit",
            title: b.filename?.replace(/\.pdf$/i, "") || form.title || "Toplu plan",
            counterparty: form.counterparty || b.bank || "Banka",
            bank: form.bank || b.bank || "",
            installment_amount: b.rows[0]?.amount || 0,
            currency: form.currency,
            count: b.rows.length,
            first_due_date: b.rows[0]?.due_date || form.due_date,
            interval_days: parseInt(plan.interval_days),
            status: form.status,
            pdf_attachment_id: b.attachment_id,
            rows: b.rows,
          };
          if (form.manual_rate) payload.manual_rate = parseFloat(form.manual_rate);
          await axios.post(`${API}/obligations/installments`, payload);
        }
      } else if (installment) {
        const payload = {
          company_id: form.company_id, kind: form.kind || "Taksit",
          title: form.title, counterparty: form.counterparty, bank: form.bank,
          installment_amount: parseFloat(form.amount || 0), currency: form.currency,
          count: pdfResult?.rows?.length || parseInt(plan.count),
          first_due_date: form.due_date,
          interval_days: parseInt(plan.interval_days), status: form.status,
        };
        if (form.manual_rate) payload.manual_rate = parseFloat(form.manual_rate);
        if (pdfResult) {
          payload.pdf_attachment_id = pdfResult.attachment_id;
          payload.rows = pdfResult.rows;
        }
        await axios.post(`${API}/obligations/installments`, payload);
      } else {
        const payload = { ...form, amount: parseFloat(form.amount) };
        if (form.manual_rate) payload.manual_rate = parseFloat(form.manual_rate);
        else delete payload.manual_rate;
        await axios.post(`${API}/obligations`, payload);
      }
      onDone();
    } catch (ex) { setErr(ex.response?.data?.detail || "Kayıt eklenemedi"); }
    finally { setBusy(false); }
  };

  const totalRows = pdfResult?.rows || [];
  const pdfSummary = totalRows.reduce((a, r) => ({
    amount: a.amount + (parseFloat(r.amount) || 0),
    principal: a.principal + (parseFloat(r.principal) || 0),
    interest: a.interest + (parseFloat(r.interest) || 0),
    commission: a.commission + (parseFloat(r.commission) || 0),
  }), { amount: 0, principal: 0, interest: 0, commission: 0 });
  const manualTotalTry = installment ? (parseFloat(form.amount || 0) * parseInt(plan.count || 0)) : 0;

  return <Modal onClose={onClose} title={installment ? "Yeni taksit planı" : "Yeni ödeme / tahsilat"} wide={!!pdfResult}>
    <form onSubmit={submit} className="form">
      <div className="row2">
        <Field label="Şirket"><select data-testid="ob-company" value={form.company_id} onChange={e => setForm({ ...form, company_id: e.target.value })}>{meta.companies?.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}</select></Field>
        <Field label="Ödeme tipi"><select data-testid="ob-kind" value={form.kind} onChange={e => setForm({ ...form, kind: e.target.value })}>{meta.payment_kinds.map(k => <option key={k}>{k}</option>)}</select></Field>
      </div>
      <Field label="Başlık"><input data-testid="ob-title" value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} required /></Field>
      <div className="row2">
        <Field label="Karşı taraf"><input data-testid="ob-counterparty" value={form.counterparty} onChange={e => setForm({ ...form, counterparty: e.target.value })} required /></Field>
        <Field label="Banka">
          <div className="bank-select-wrap">
            {form.bank && <BankLogo name={form.bank} banks={banks} size={22} />}
            <select data-testid="ob-bank" value={form.bank} onChange={e => setForm({ ...form, bank: e.target.value })}>
              <option value="">Banka seç…</option>
              {banks.map(b => <option key={b.id} value={b.name}>{b.name}</option>)}
            </select>
          </div>
        </Field>
      </div>
      <div className="row2">
        <Field label={installment ? "Taksit tutarı (otomatik)" : "Tutar"}><input data-testid="ob-amount" type="number" step="0.01" value={form.amount} onChange={e => setForm({ ...form, amount: e.target.value })} required={!pdfResult} /></Field>
        <Field label="Para birimi"><select data-testid="ob-currency" value={form.currency} onChange={e => setForm({ ...form, currency: e.target.value })}>{["TRY", "USD", "EUR", "GBP"].map(c => <option key={c}>{c}</option>)}</select></Field>
      </div>
      {form.currency !== "TRY" && <Field label="Manuel kur (opsiyonel)"><input data-testid="ob-manual-rate" type="number" step="0.0001" value={form.manual_rate} onChange={e => setForm({ ...form, manual_rate: e.target.value })} /></Field>}
      <Field label={installment ? "İlk vade tarihi" : "Vade tarihi"}><input data-testid="ob-due" type="date" value={form.due_date} onChange={e => setForm({ ...form, due_date: e.target.value })} required /></Field>

      <label className="toggle" data-testid="installment-toggle-wrap">
        <input type="checkbox" data-testid="installment-toggle" checked={installment} onChange={e => { setInstallment(e.target.checked); if (!e.target.checked) { setPdfResult(null); setPdfBatch([]); } }} />
        <span>Taksit planı oluştur (otomatik dağıt veya PDF yükle · çoklu seçim)</span>
      </label>

      {installment && !pdfResult && pdfBatch.length === 0 && <>
        <div className="row2 installment-row">
          <Field label="Taksit sayısı"><input data-testid="plan-count" type="number" min="2" max="120" value={plan.count} onChange={e => setPlan({ ...plan, count: e.target.value })} required /></Field>
          <Field label="Aralık (gün)"><input data-testid="plan-interval" type="number" min="1" value={plan.interval_days} onChange={e => setPlan({ ...plan, interval_days: e.target.value })} required /></Field>
        </div>
        {form.amount && plan.count > 0 && <div className="plan-preview" data-testid="plan-preview">
          <strong>Önizleme:</strong> {plan.count} taksit × {money(parseFloat(form.amount), form.currency)} = <b>{money(manualTotalTry, form.currency)}</b>
        </div>}
        <div className="pdf-drop">
          <label className="outline-btn pdf-upload-btn" data-testid="pdf-upload-btn">
            {pdfParsing ? <><Loader2 size={16} className="spin" /> AI PDF okuyor…</> : <><FileUp size={16} /> Banka PDF'inden otomatik yükle (çoklu)</>}
            <input type="file" accept="application/pdf,.pdf" hidden multiple onChange={handlePdfUpload} disabled={pdfParsing} data-testid="pdf-file-input" />
          </label>
          <small>Claude Sonnet 5.5 ile banka ödeme planı PDF'lerinizi otomatik tabloya çevirir. Birden fazla PDF seçebilirsiniz — her biri ayrı taksit planı olur.</small>
        </div>
      </>}

      {installment && pdfBatch.length > 0 && <div className="pdf-batch" data-testid="pdf-batch">
        <div className="pdf-review-head">
          <div>
            <strong>Toplu PDF önizlemesi · {pdfBatch.length} plan</strong>
            <span>Toplam {pdfBatch.reduce((a, b) => a + (b.rows?.length || 0), 0)} taksit tespit edildi</span>
          </div>
          <button type="button" className="link-btn" onClick={() => setPdfBatch([])}><X size={14} /> Tümünü temizle</button>
        </div>
        <div className="batch-list">
          {pdfBatch.map((b, idx) => {
            const total = (b.rows || []).reduce((a, r) => a + (parseFloat(r.amount) || 0), 0);
            return <div className="batch-item" key={idx} data-testid={`batch-item-${idx}`}>
              <div className="batch-item-head">
                <Paperclip size={14} />
                <strong>{b.filename}</strong>
                <span>{b.rows?.length || 0} satır · banka: {b.bank || "tespit edilemedi"}</span>
                <b className="batch-total">{money(total, form.currency)}</b>
                <button type="button" className="icon-btn-sm" onClick={() => setPdfBatch(pdfBatch.filter((_, i) => i !== idx))} data-testid={`batch-remove-${idx}`}><X size={12} /></button>
              </div>
              <div className="batch-item-rows">
                {(b.rows || []).slice(0, 3).map((r, i) => <span key={i} className="batch-row-chip">{r.due_date}: {money(parseFloat(r.amount) || 0, form.currency)}</span>)}
                {b.rows?.length > 3 && <span className="batch-row-chip">+{b.rows.length - 3} daha</span>}
              </div>
            </div>;
          })}
        </div>
        <small className="muted">Kaydettiğinizde her PDF ayrı taksit planı olarak oluşturulacak.</small>
      </div>}

      {installment && pdfResult && <div className="pdf-review" data-testid="pdf-review">
        <div className="pdf-review-head">
          <div>
            <strong>PDF önizlemesi · {pdfResult.filename}</strong>
            <span>{pdfResult.rows.length} satır tespit edildi{pdfResult.bank && ` · banka: ${pdfResult.bank}`}</span>
          </div>
          <button type="button" className="link-btn" onClick={() => setPdfResult(null)} data-testid="pdf-clear"><X size={14} /> Temizle</button>
        </div>
        <div className="pdf-table-scroll"><table className="pdf-table">
          <thead><tr><th>#</th><th>Vade</th><th>Taksit</th><th>Anapara</th><th>Faiz</th><th>Komisyon</th><th></th></tr></thead>
          <tbody>{pdfResult.rows.map((r, i) => <tr key={i} data-testid={`pdf-row-${i}`}>
            <td>{i + 1}</td>
            <td><input type="date" value={r.due_date || ""} onChange={e => updatePdfRow(i, "due_date", e.target.value)} /></td>
            <td><input type="number" step="0.01" value={r.amount ?? ""} onChange={e => updatePdfRow(i, "amount", e.target.value)} /></td>
            <td><input type="number" step="0.01" value={r.principal ?? ""} onChange={e => updatePdfRow(i, "principal", e.target.value)} placeholder="—" /></td>
            <td><input type="number" step="0.01" value={r.interest ?? ""} onChange={e => updatePdfRow(i, "interest", e.target.value)} placeholder="—" /></td>
            <td><input type="number" step="0.01" value={r.commission ?? ""} onChange={e => updatePdfRow(i, "commission", e.target.value)} placeholder="—" /></td>
            <td><button type="button" className="icon-btn-sm" onClick={() => removePdfRow(i)} data-testid={`pdf-remove-${i}`}><X size={12} /></button></td>
          </tr>)}</tbody>
          <tfoot><tr>
            <td colSpan="2"><b>Toplam</b></td>
            <td><b>{money(pdfSummary.amount, form.currency)}</b></td>
            <td><b>{money(pdfSummary.principal, form.currency)}</b></td>
            <td><b className="interest-text">{money(pdfSummary.interest, form.currency)}</b></td>
            <td><b className="interest-text">{money(pdfSummary.commission, form.currency)}</b></td>
            <td></td>
          </tr></tfoot>
        </table></div>
        <small className="muted">Satırları kontrol edin, gerekirse düzenleyin. Kaydettiğinizde PDF ek olarak saklanır ve finans gider hesabına yansır.</small>
      </div>}

      {err && <div className="auth-err">{String(err)}</div>}
      <div className="form-actions"><button type="button" className="outline-btn" onClick={onClose}>Vazgeç</button><button data-testid="ob-submit" className="primary-btn" disabled={busy}>{busy ? "Kaydediliyor…" : (pdfResult ? `${pdfResult.rows.length} taksit onayla` : pdfBatch.length > 0 ? `${pdfBatch.length} planı kaydet` : "Kaydet")}</button></div>
    </form>
  </Modal>;
}

// ---------- USERS PAGE (admin only) ----------
function UsersPage() {
  const [users, setUsers] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [err, setErr] = useState("");
  const { user: me } = useAuth();
  const load = () => axios.get(`${API}/users`).then(r => setUsers(r.data));
  useEffect(() => { load(); axios.get(`${API}/companies`).then(r => setCompanies(r.data)); }, []);
  const changeRole = async (uid, role) => {
    try { await axios.patch(`${API}/users/${uid}`, { role }); load(); setErr(""); }
    catch (ex) { setErr(ex.response?.data?.detail || "Güncellenemedi"); }
  };
  const toggleCompany = async (u, cid) => {
    const current = u.allowed_companies || [];
    const next = current.includes(cid) ? current.filter(x => x !== cid) : [...current, cid];
    try { await axios.patch(`${API}/users/${u.id}`, { allowed_companies: next }); load(); setErr(""); }
    catch (ex) { setErr(ex.response?.data?.detail || "Güncellenemedi"); }
  };
  const clearRestriction = async (u) => {
    try { await axios.patch(`${API}/users/${u.id}`, { allowed_companies: [] }); load(); setErr(""); }
    catch (ex) { setErr(ex.response?.data?.detail || "Güncellenemedi"); }
  };
  const removeUser = async (uid) => {
    if (!window.confirm("Bu kullanıcıyı silmek istediğinizden emin misiniz?")) return;
    try { await axios.delete(`${API}/users/${uid}`); load(); setErr(""); }
    catch (ex) { setErr(ex.response?.data?.detail || "Silinemedi"); }
  };
  const approveUser = async (u, role) => {
    try {
      await axios.post(`${API}/admin/users/${u.id}/approve`, { role, allowed_companies: u.allowed_companies || [] });
      load(); setErr("");
    } catch (ex) { setErr(ex.response?.data?.detail || "Onaylanamadı"); }
  };
  const rejectUser = async (u) => {
    if (!window.confirm("Bu kullanıcıyı reddetmek istediğinize emin misiniz?")) return;
    try { await axios.post(`${API}/admin/users/${u.id}/reject`); load(); setErr(""); }
    catch (ex) { setErr(ex.response?.data?.detail || "Reddedilemedi"); }
  };
  const pending = users.filter(u => u.status === "pending");
  const activeUsers = users.filter(u => u.status !== "pending");
  return <div className="page">
    <section className="page-toolbar">
      <div><p className="eyebrow">EKİP YÖNETİMİ</p><h2>Kullanıcılar, Roller ve Şirket Görünürlüğü</h2><p className="muted">{users.length} kullanıcı · adminler tüm şirketleri görür ve değiştirebilir · finans/görüntüleyici için şirket kısıtlaması atanabilir</p></div>
      <div className="toolbar-actions"><button className="primary-btn" data-testid="add-user-btn" onClick={() => setShowForm(true)}><Plus size={17} /> Yeni kullanıcı</button></div>
    </section>
    {err && <div className="auth-err" style={{ marginBottom: 14 }}>{String(err)}</div>}

    {pending.length > 0 && <section className="panel full-panel" data-testid="pending-users-panel" style={{ marginBottom: 18, borderLeft: "3px solid #f59e0b" }}>
      <div style={{ padding: "12px 16px", borderBottom: "1px solid var(--border)" }}>
        <strong>Onay bekleyen Google kullanıcıları ({pending.length})</strong>
        <p className="muted" style={{ fontSize: 12, margin: "4px 0 0" }}>Google ile giriş yapan yeni kullanıcılar burada görünür. Rol ve şirket erişimi atayarak onaylayın.</p>
      </div>
      <div className="table-scroll"><table>
        <thead><tr><th>İSİM</th><th>E-POSTA</th><th>GELİŞ TARİHİ</th><th>İŞLEM</th></tr></thead>
        <tbody>{pending.map(u => <tr key={u.id} data-testid={`pending-user-${u.id}`}>
          <td><strong>{u.name || "—"}</strong></td>
          <td>{u.email}</td>
          <td>{(u.created_at || "").slice(0, 10)}</td>
          <td style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
            <button className="primary-btn" style={{ padding: "4px 10px", fontSize: 12 }} data-testid={`approve-viewer-${u.id}`} onClick={() => approveUser(u, "viewer")}>Viewer olarak onayla</button>
            <button className="primary-btn" style={{ padding: "4px 10px", fontSize: 12 }} data-testid={`approve-finance-${u.id}`} onClick={() => approveUser(u, "finance")}>Finance olarak onayla</button>
            <button className="outline-btn" style={{ padding: "4px 10px", fontSize: 12, color: "#dc2626", borderColor: "#dc2626" }} data-testid={`reject-user-${u.id}`} onClick={() => rejectUser(u)}>Reddet</button>
          </td>
        </tr>)}</tbody>
      </table></div>
    </section>}

    <section className="panel full-panel">
      <div className="table-scroll"><table>
        <thead><tr><th>İSİM</th><th>E-POSTA</th><th>ROL</th><th>ŞİRKET ERİŞİMİ</th><th>OLUŞTURMA</th><th></th></tr></thead>
        <tbody>{activeUsers.map(u => {
          const isAdmin = u.role === "admin";
          const allowed = u.allowed_companies || [];
          return <tr key={u.id} data-testid={`user-row-${u.id}`}>
          <td><strong>{u.name || "—"}</strong>{u.id === me?.id && <span style={{ color: "#a970ff" }}>Siz</span>}{u.auth_provider === "google" && <span style={{ fontSize: 10, marginLeft: 6, padding: "1px 6px", background: "#dbeafe", color: "#1e40af", borderRadius: 8 }}>Google</span>}</td>
          <td>{u.email}</td>
          <td>
            <select data-testid={`user-role-${u.id}`} value={u.role} onChange={e => changeRole(u.id, e.target.value)} disabled={u.id === me?.id} className="inline-select">
              <option value="admin">Admin</option>
              <option value="finance">Finans</option>
              <option value="viewer">Görüntüleyici</option>
            </select>
          </td>
          <td>
            {isAdmin ? <span className="muted" style={{ fontSize: 12 }}>Tüm şirketler (admin)</span> : (
              <div style={{ display: "flex", flexWrap: "wrap", gap: 4, alignItems: "center" }} data-testid={`user-companies-${u.id}`}>
                {companies.map(c => {
                  const on = allowed.length === 0 || allowed.includes(c.id);
                  return <button key={c.id} data-testid={`user-company-${u.id}-${c.id}`} onClick={() => toggleCompany(u, c.id)}
                    style={{
                      padding: "2px 8px", borderRadius: 10, fontSize: 11, cursor: "pointer",
                      border: `1px solid ${on ? "#1e40af" : "#e5e7eb"}`,
                      background: on ? "#dbeafe" : "white",
                      color: on ? "#1e40af" : "#9ca3af",
                      fontWeight: on ? 600 : 400,
                    }}>
                    {c.name}
                  </button>;
                })}
                {allowed.length > 0 && <button className="icon-btn-sm" onClick={() => clearRestriction(u)} data-testid={`user-clear-${u.id}`} title="Kısıtlamayı temizle"><X size={12} /></button>}
                {allowed.length === 0 && <span className="muted" style={{ fontSize: 11, marginLeft: 4 }}>(hepsi)</span>}
              </div>
            )}
          </td>
          <td>{(u.created_at || "").slice(0, 10)}</td>
          <td>{u.id !== me?.id && <button className="icon-btn-sm" data-testid={`user-delete-${u.id}`} onClick={() => removeUser(u.id)}><Trash2 size={14} /></button>}</td>
        </tr>;})}</tbody>
      </table></div>
    </section>
    {showForm && <UserForm onClose={() => setShowForm(false)} onDone={() => { setShowForm(false); load(); }} />}
  </div>;
}

function UserForm({ onClose, onDone }) {
  const [form, setForm] = useState({ email: "", name: "", password: "", role: "viewer" });
  const [err, setErr] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/auth/register`, form);
      onDone();
    } catch (ex) { setErr(ex.response?.data?.detail || "Oluşturulamadı"); }
  };
  return <Modal onClose={onClose} title="Yeni kullanıcı">
    <form onSubmit={submit} className="form">
      <Field label="İsim"><input data-testid="user-name" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} required /></Field>
      <Field label="E-posta"><input data-testid="user-email" type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} required /></Field>
      <Field label="Şifre"><input data-testid="user-password" type="password" value={form.password} onChange={e => setForm({ ...form, password: e.target.value })} minLength="4" required /></Field>
      <Field label="Rol"><select data-testid="user-role-select" value={form.role} onChange={e => setForm({ ...form, role: e.target.value })}>
        <option value="viewer">Görüntüleyici</option>
        <option value="finance">Finans</option>
        <option value="admin">Admin</option>
      </select></Field>
      {err && <div className="auth-err">{String(err)}</div>}
      <div className="form-actions"><button type="button" className="outline-btn" onClick={onClose}>Vazgeç</button><button data-testid="user-submit" className="primary-btn">Oluştur</button></div>
    </form>
  </Modal>;
}

// ---------- ESCROWS PAGE (cari style) ----------
function EscrowsPage({ company, companies }) {
  const { user } = useAuth();
  const canEdit = user?.role !== "viewer";
  const [cards, setCards] = useState([]);
  const [dash, setDash] = useState(null);
  const [openCards, setOpenCards] = useState({});
  const [showEntryForm, setShowEntryForm] = useState(null);
  const [showCardForm, setShowCardForm] = useState(false);
  const load = () => {
    axios.get(`${API}/escrow-cards`).then(r => setCards(r.data));
    axios.get(`${API}/escrow-dashboard`).then(r => setDash(r.data));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [company]);
  const removeEntry = async (eid) => {
    if (!window.confirm("Bu emaneti silmek istediğinizden emin misiniz?")) return;
    await axios.delete(`${API}/escrow-entries/${eid}`); load();
  };
  const returnEntry = async (e) => {
    const input = window.prompt(`${e.title}\nAlınan: ${e.amount} ${e.currency}\nİade edilen tutarı girin:`, e.expected_return_amount || e.amount);
    if (input == null) return;
    const amt = parseFloat(input); if (isNaN(amt)) return;
    await axios.patch(`${API}/escrow-entries/${e.id}`, { status: "İade edildi", returned_amount: amt });
    load();
  };
  return <div className="page">
    <section className="page-toolbar">
      <div><p className="eyebrow">EMANET</p><h2>Cari Bazlı Emanet Takibi</h2>
        <p className="muted">{cards.length} kart · {dash?.active_entries || 0} aktif emanet</p></div>
      {canEdit && <div className="toolbar-actions">
        <button className="outline-btn" data-testid="add-card-btn" onClick={() => setShowCardForm(true)}><Plus size={15} /> Yeni cari kart</button>
        <button className="outline-btn" data-testid="add-escrow-in-btn" onClick={() => setShowEntryForm({ direction: "in" })}><ArrowDownRight size={15} /> Giriş</button>
        <button className="primary-btn" data-testid="add-escrow-out-btn" onClick={() => setShowEntryForm({ direction: "out" })}><ArrowUpRight size={15} /> Çıkış</button>
      </div>}
    </section>
    {dash && <>
      <div className="totals-row escrow-totals">
        <div className="total-card tl"><span>TL eşdeğeri (net)</span><strong>{money(dash.total_try)}</strong></div>
        {["TRY", "USD", "EUR", "GBP"].map(c => (
          <div key={c} className="total-card"><span>{c}</span><strong>{money(dash.totals_by_currency[c] || 0, c)}</strong></div>
        ))}
      </div>
      <div className="escrow-split">
        <div className="panel split-card"><span>Kurum emanetleri</span><strong>{money(dash.corporate_total_try)}</strong><small>{cards.filter(c => c.type === "corporate").length} cari kart</small></div>
        <div className="panel split-card"><span>Şahıs emanetleri</span><strong>{money(dash.individual_total_try)}</strong><small>{cards.filter(c => c.type === "individual").length} cari kart</small></div>
      </div>
    </>}
    {cards.length === 0 && <div className="panel empty">Henüz cari kart yok. "Yeni cari kart" ile başlayın.</div>}
    {cards.map(c => {
      const open = openCards[c.id];
      return <section className="panel escrow-card-panel" key={c.id} data-testid={`escrow-card-${c.id}`}>
        <button type="button" className="escrow-card-head" onClick={() => setOpenCards({ ...openCards, [c.id]: !open })}>
          <div className="ec-left">
            <div className={`ec-ico ${c.type}`}>{c.type === "corporate" ? <Landmark size={18} /> : <UsersRound size={18} />}</div>
            <div><strong>{c.name}</strong><span>{c.type === "corporate" ? "Kurum" : "Şahıs"} · {c.active_count} aktif · {c.notes}</span></div>
          </div>
          <div className="ec-stats">
            <div><span>Net TL</span><b className={c.net_try >= 0 ? "income" : "expense"}>{money(c.net_try)}</b></div>
          </div>
          <ChevronDown size={16} className={`chev ${open ? "open" : ""}`} />
        </button>
        {open && <div className="ec-entries">
          {c.entries.length === 0 ? <div className="empty">Bu karta henüz emanet eklenmemiş.</div> :
            <div className="table-scroll"><table>
              <thead><tr><th>YÖN</th><th>BAŞLIK</th><th>ŞİRKET</th><th>TUTAR</th><th>BEKLENEN İADE</th><th>ALIŞ</th><th>İADE TARİHİ</th><th>DURUM</th><th>ZARAR</th><th></th></tr></thead>
              <tbody>{c.entries.map(e => {
                const comp = companies.find(cc => cc.id === e.company_id);
                const expected = e.expected_return_amount != null ? parseFloat(e.expected_return_amount) : null;
                const actualReturned = e.returned_amount != null ? parseFloat(e.returned_amount) : null;
                const loss = actualReturned != null && expected != null ? expected - actualReturned : null;
                return <tr key={e.id} data-testid={`entry-${e.id}`}>
                  <td><span className={`direction-chip ${e.direction || "in"}`}>{e.direction === "out" ? "Çıkış" : "Giriş"}</span></td>
                  <td><strong>{e.title}</strong><span>{e.notes}</span></td>
                  <td><span className="company-pill" style={{ background: `${comp?.color}22`, color: comp?.color }}>{comp?.short}</span></td>
                  <td>{money(e.amount, e.currency)}</td>
                  <td>{expected != null ? money(expected, e.currency) : "—"}</td>
                  <td>{e.received_date}</td>
                  <td>{e.return_date}</td>
                  <td><span className={`status-pill ${e.status === "İade edildi" ? "done" : ""}`}>{e.status}</span></td>
                  <td className={loss && loss > 0 ? "expense" : "income"}>{loss != null ? money(loss, e.currency) : "—"}</td>
                  <td>
                    {canEdit && e.status !== "İade edildi" && <button className="link-btn" onClick={() => returnEntry(e)} data-testid={`return-${e.id}`}>İade et</button>}
                    {canEdit && <button className="icon-btn-sm" onClick={() => removeEntry(e.id)} data-testid={`del-${e.id}`}><Trash2 size={12} /></button>}
                  </td>
                </tr>;
              })}</tbody>
            </table></div>
          }
        </div>}
      </section>;
    })}
    {showCardForm && <EscrowCardForm onClose={() => setShowCardForm(false)} onDone={() => { setShowCardForm(false); load(); }} />}
    {showEntryForm && <EscrowEntryForm company={company} companies={companies} cards={cards} direction={showEntryForm.direction} onClose={() => setShowEntryForm(null)} onDone={() => { setShowEntryForm(null); load(); }} />}
  </div>;
}

function EscrowCardForm({ onClose, onDone }) {
  const [form, setForm] = useState({ type: "corporate", name: "", tax_no: "", phone: "", email: "", notes: "" });
  const [err, setErr] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    try { await axios.post(`${API}/escrow-cards`, form); onDone(); }
    catch (ex) { setErr(ex.response?.data?.detail || "Kart oluşturulamadı"); }
  };
  return <Modal onClose={onClose} title="Yeni cari kart">
    <form className="form" onSubmit={submit}>
      <Field label="Tür">
        <div className="radio-row">
          <label className={`radio-chip ${form.type === "corporate" ? "active" : ""}`}><input type="radio" value="corporate" checked={form.type === "corporate"} onChange={() => setForm({ ...form, type: "corporate" })} /> Kurum</label>
          <label className={`radio-chip ${form.type === "individual" ? "active" : ""}`}><input type="radio" value="individual" checked={form.type === "individual"} onChange={() => setForm({ ...form, type: "individual" })} /> Şahıs</label>
        </div>
      </Field>
      <Field label="Ad / Unvan"><input data-testid="ec-name" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} required /></Field>
      <div className="row2">
        <Field label="Vergi/TC No"><input value={form.tax_no} onChange={e => setForm({ ...form, tax_no: e.target.value })} /></Field>
        <Field label="Telefon"><input value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} /></Field>
      </div>
      <Field label="E-posta"><input type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} /></Field>
      <Field label="Notlar"><textarea rows="2" value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} /></Field>
      {err && <div className="auth-err">{String(err)}</div>}
      <div className="form-actions"><button type="button" className="outline-btn" onClick={onClose}>Vazgeç</button><button className="primary-btn" data-testid="ec-submit">Kaydet</button></div>
    </form>
  </Modal>;
}

function EscrowEntryForm({ company, companies, cards, direction, onClose, onDone }) {
  const [form, setForm] = useState({
    direction, company_id: (company === "all" ? (companies[0]?.id || "") : company),
    title: "", card_id: cards[0]?.id || "", amount: "", currency: "TRY", manual_rate: "",
    received_date: new Date().toISOString().slice(0, 10),
    return_date: new Date(Date.now() + 30 * 86400000).toISOString().slice(0, 10),
    expected_return_amount: "", notes: "", status: "Bekliyor",
  });
  const [err, setErr] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form, amount: parseFloat(form.amount) };
      if (form.manual_rate) payload.manual_rate = parseFloat(form.manual_rate); else delete payload.manual_rate;
      if (form.expected_return_amount) payload.expected_return_amount = parseFloat(form.expected_return_amount); else delete payload.expected_return_amount;
      await axios.post(`${API}/escrow-entries`, payload);
      onDone();
    } catch (ex) { setErr(ex.response?.data?.detail || "Kayıt eklenemedi"); }
  };
  const expected = parseFloat(form.expected_return_amount || 0);
  const given = parseFloat(form.amount || 0);
  const estimatedLoss = expected && given ? given - expected : 0;
  return <Modal onClose={onClose} title={direction === "out" ? "Emanet çıkışı" : "Emanet girişi"}>
    <form className="form" onSubmit={submit}>
      <div className="row2">
        <Field label="Şirket"><select value={form.company_id} onChange={e => setForm({ ...form, company_id: e.target.value })}>{companies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}</select></Field>
        <Field label="Karşı taraf (cari kart)">
          <select data-testid="ee-card" value={form.card_id} onChange={e => setForm({ ...form, card_id: e.target.value })} required>
            <option value="">Kart seç…</option>
            {cards.map(c => <option key={c.id} value={c.id}>{c.name} ({c.type === "corporate" ? "Kurum" : "Şahıs"})</option>)}
          </select>
        </Field>
      </div>
      <Field label="Başlık"><input data-testid="ee-title" value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} required /></Field>
      <div className="row2">
        <Field label="Alınan tutar"><input data-testid="ee-amount" type="number" step="0.01" value={form.amount} onChange={e => setForm({ ...form, amount: e.target.value })} required /></Field>
        <Field label="Para birimi"><select value={form.currency} onChange={e => setForm({ ...form, currency: e.target.value })}>{["TRY", "USD", "EUR", "GBP"].map(c => <option key={c}>{c}</option>)}</select></Field>
      </div>
      <Field label="Beklenen iade tutarı (farklıysa)"><input data-testid="ee-expected" type="number" step="0.01" value={form.expected_return_amount} onChange={e => setForm({ ...form, expected_return_amount: e.target.value })} placeholder={form.amount || "0"} /></Field>
      {estimatedLoss > 0 && <div className="plan-preview"><strong>Tahmini zarar:</strong> <b className="interest-text">{money(estimatedLoss, form.currency)}</b> — Finans Giderleri'ne otomatik yansır</div>}
      <div className="row2">
        <Field label="Alış tarihi"><input type="date" value={form.received_date} onChange={e => setForm({ ...form, received_date: e.target.value })} required /></Field>
        <Field label="İade tarihi"><input type="date" value={form.return_date} onChange={e => setForm({ ...form, return_date: e.target.value })} required /></Field>
      </div>
      <Field label="Notlar"><textarea rows="2" value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} /></Field>
      {err && <div className="auth-err">{String(err)}</div>}
      <div className="form-actions"><button type="button" className="outline-btn" onClick={onClose}>Vazgeç</button><button className="primary-btn" data-testid="ee-submit">Kaydet</button></div>
    </form>
  </Modal>;
}

// ---------- CHECKS PAGE ----------
function ChecksPage({ company, companies }) {  const { user } = useAuth();
  const canEdit = user?.role !== "viewer";
  const [data, setData] = useState(null);
  const [period, setPeriod] = useState("all");
  const [recipient, setRecipient] = useState("all");
  const [checkKind, setCheckKind] = useState("all");
  const [custom, setCustom] = useState({ start: "", end: "" });
  const [showForm, setShowForm] = useState(false);
  const [persons, setPersons] = useState([]);
  const [meta, setMeta] = useState({ banks: [], check_kinds: ["Ticari", "Hatır"] });
  useEffect(() => {
    axios.get(`${API}/persons`, { params: { type: "corporate" } }).then(r => setPersons(r.data));
    axios.get(`${API}/meta`).then(r => setMeta(r.data));
  }, []);
  const load = () => {
    const params = { period, company_id: company };
    if (recipient !== "all") params.recipient_person_id = recipient;
    if (checkKind !== "all") params.check_kind = checkKind;
    if (period === "custom") { params.start = custom.start; params.end = custom.end; if (!params.start || !params.end) return; }
    axios.get(`${API}/checks`, { params }).then(r => setData(r.data));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [period, company, recipient, checkKind, custom]);
  const periodTabs = [["all", "Tümü"], ["day", "Günlük"], ["week", "Haftalık"], ["month", "Aylık"], ["year", "Yıllık"], ["custom", "Özel"]];
  const corporatePersons = persons.filter(p => p.type === "corporate" && p.status === "approved");
  const del = async (cid) => { if (!window.confirm("Çek silinsin mi?")) return; await axios.delete(`${API}/checks/${cid}`); load(); };
  const [importMsg, setImportMsg] = useState(null);
  const [importing, setImporting] = useState(false);
  const downloadTemplate = async () => {
    const res = await axios.get(`${API}/checks/template`, { responseType: "blob" });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement("a"); a.href = url; a.download = "mesfin-cek-sablonu.xlsx";
    document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
  };
  const importExcel = async (ev) => {
    const file = ev.target.files?.[0]; if (!file) return;
    const form = new FormData(); form.append("file", file);
    setImporting(true); setImportMsg(null);
    try {
      const { data: r } = await axios.post(`${API}/checks/import`, form);
      setImportMsg({ type: "success", text: r.message, errors: r.errors || [] });
      load();
    } catch (e) {
      setImportMsg({ type: "error", text: e.response?.data?.detail || "İçe aktarma başarısız" });
    } finally { setImporting(false); ev.target.value = ""; }
  };
  return <div className="page">
    <section className="page-toolbar">
      <div><p className="eyebrow">ÇEKLER</p><h2>Çek Bordrosu ve Vade Takibi</h2>
        <p className="muted">{data ? `${data.count} çek · ${money(data.total_try)} TL / ${money(data.total_eur, "EUR")}` : "Yükleniyor…"}</p></div>
      {canEdit && <div className="toolbar-actions">
        <button className="outline-btn" data-testid="check-template-btn" onClick={downloadTemplate}><Download size={15} /> Şablonu indir</button>
        <label className="outline-btn" data-testid="check-import-btn" style={{ cursor: importing ? "wait" : "pointer" }}>
          {importing ? <><Loader2 size={14} className="spin" /> Yükleniyor...</> : <><Upload size={15} /> Excel ile veri gir</>}
          <input type="file" accept=".xlsx,.xls" hidden onChange={importExcel} disabled={importing} />
        </label>
        <button className="primary-btn" data-testid="add-check-btn" onClick={() => setShowForm(true)}><Plus size={17} /> Yeni çek</button>
      </div>}
    </section>
    {importMsg && <div className="panel" style={{ padding: 14, marginBottom: 14, background: importMsg.type === "success" ? "#eff6ff" : "#fef2f2", borderLeft: `4px solid ${importMsg.type === "success" ? "#1e40af" : "#dc2626"}` }} data-testid="check-import-msg">
      <strong>{importMsg.text}</strong>
      {importMsg.errors?.length > 0 && <details style={{ marginTop: 8 }}>
        <summary style={{ cursor: "pointer", fontSize: 13 }}>Hata detayları ({importMsg.errors.length})</summary>
        <ul style={{ fontSize: 12, marginTop: 6, paddingLeft: 20 }}>
          {importMsg.errors.map((er, i) => <li key={i}>Satır {er.row}: {er.reason}</li>)}
        </ul>
      </details>}
    </div>}
    <div className="cal-controls">
      <div className="period-tabs" data-testid="check-period-tabs">
        {periodTabs.map(([k, l]) => <button key={k} data-testid={`check-period-${k}`} className={period === k ? "active" : ""} onClick={() => setPeriod(k)}>{l}</button>)}
      </div>
      <div className="filters">
        <select data-testid="filter-check-kind" value={checkKind} onChange={e => setCheckKind(e.target.value)}>
          <option value="all">Tüm tipler</option>
          {meta.check_kinds.map(k => <option key={k}>{k}</option>)}
        </select>
        <select data-testid="filter-recipient" value={recipient} onChange={e => setRecipient(e.target.value)}>
          <option value="all">Tüm firmalar</option>
          {corporatePersons.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
        </select>
        {period === "custom" && <>
          <input type="date" value={custom.start} onChange={e => setCustom({ ...custom, start: e.target.value })} />
          <input type="date" value={custom.end} onChange={e => setCustom({ ...custom, end: e.target.value })} />
        </>}
      </div>
    </div>
    {data && <>
      <div className="totals-row">
        <div className="total-card tl"><span>TL Eşdeğeri Toplam</span><strong>{money(data.total_try)}</strong></div>
        <div className="total-card tl eur"><span>EUR Eşdeğeri</span><strong>{money(data.total_eur, "EUR")}</strong></div>
        {["TRY", "USD", "EUR", "GBP"].map(c => (
          <div key={c} className="total-card"><span>{c}</span><strong>{money(data.totals_by_currency[c] || 0, c)}</strong></div>
        ))}
      </div>
      <section className="panel full-panel">
        {data.checks.length === 0 ? <div className="empty">Bu aralıkta çek yok.</div> :
          <div className="table-scroll"><table>
            <thead><tr><th>TİP</th><th>BANKA</th><th>VERİLDİĞİ TARİH</th><th>VADE</th><th>ÇEKİ ALAN</th><th>AÇIKLAMA</th><th>TUTAR</th><th>TL EŞ.</th><th>EUR EŞ.</th><th></th></tr></thead>
            <tbody>{data.checks.map(c => {
              const comp = companies.find(cc => cc.id === c.company_id);
              return <tr key={c.id} data-testid={`check-row-${c.id}`}>
                <td><span className={`kind-pill ${c.check_kind === "Hatır" ? "hatir-pill" : ""}`}>{c.check_kind}</span></td>
                <td><BankCell name={c.bank} banks={meta.banks} /></td>
                <td>{c.issue_date}</td>
                <td>{c.due_date}</td>
                <td><strong>{c.recipient_name}</strong><span className="company-pill" style={{ background: `${comp?.color}22`, color: comp?.color }}>{comp?.short}</span></td>
                <td>{c.description || "—"}</td>
                <td>{money(c.amount, c.currency)}</td>
                <td><b>{money(c.try_equivalent)}</b></td>
                <td>{money(c.eur_equivalent, "EUR")}</td>
                <td>{canEdit && <button className="icon-btn-sm" onClick={() => del(c.id)} data-testid={`check-del-${c.id}`}><Trash2 size={12} /></button>}</td>
              </tr>;
            })}</tbody>
          </table></div>
        }
      </section>
    </>}
    {showForm && <CheckForm company={company} companies={companies} meta={meta} persons={corporatePersons} onClose={() => setShowForm(false)} onDone={() => { setShowForm(false); load(); }} />}
  </div>;
}

function CheckForm({ company, companies, meta, persons, onClose, onDone }) {
  const [form, setForm] = useState({
    company_id: (company === "all" ? (companies[0]?.id || "") : company),
    check_kind: "Ticari", bank: "",
    issue_date: new Date().toISOString().slice(0, 10),
    due_date: new Date(Date.now() + 30 * 86400000).toISOString().slice(0, 10),
    recipient_person_id: persons[0]?.id || "",
    amount: "", currency: "TRY", manual_rate: "", description: "",
  });
  const [err, setErr] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form, amount: parseFloat(form.amount) };
      if (form.manual_rate) payload.manual_rate = parseFloat(form.manual_rate); else delete payload.manual_rate;
      await axios.post(`${API}/checks`, payload);
      onDone();
    } catch (ex) { setErr(ex.response?.data?.detail || "Çek kaydedilemedi"); }
  };
  return <Modal onClose={onClose} title="Yeni çek">
    <form className="form" onSubmit={submit}>
      <div className="row2">
        <Field label="Çek tipi"><select data-testid="chk-kind" value={form.check_kind} onChange={e => setForm({ ...form, check_kind: e.target.value })}>{meta.check_kinds.map(k => <option key={k}>{k}</option>)}</select></Field>
        <Field label="Şirket (operasyonel)"><select value={form.company_id} onChange={e => setForm({ ...form, company_id: e.target.value })}>{companies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}</select></Field>
      </div>
      <Field label="Banka">
        <div className="bank-select-wrap">
          {form.bank && <BankLogo name={form.bank} banks={meta.banks} size={22} />}
          <select data-testid="chk-bank" value={form.bank} onChange={e => setForm({ ...form, bank: e.target.value })} required>
            <option value="">Banka seç…</option>
            {meta.banks.map(b => <option key={b.id} value={b.name}>{b.name}</option>)}
          </select>
        </div>
      </Field>
      <div className="row2">
        <Field label="Çekin verildiği tarih"><input data-testid="chk-issue" type="date" value={form.issue_date} onChange={e => setForm({ ...form, issue_date: e.target.value })} required /></Field>
        <Field label="Ödeme vadesi tarihi"><input data-testid="chk-due" type="date" value={form.due_date} onChange={e => setForm({ ...form, due_date: e.target.value })} required /></Field>
      </div>
      <Field label="Çeki alan firma">
        <select data-testid="chk-recipient" value={form.recipient_person_id} onChange={e => setForm({ ...form, recipient_person_id: e.target.value })} required>
          <option value="">Firma seç…</option>
          {persons.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
        </select>
      </Field>
      <div className="row2">
        <Field label="Tutar"><input data-testid="chk-amount" type="number" step="0.01" value={form.amount} onChange={e => setForm({ ...form, amount: e.target.value })} required /></Field>
        <Field label="Para birimi"><select value={form.currency} onChange={e => setForm({ ...form, currency: e.target.value })}>{["TRY", "USD", "EUR", "GBP"].map(c => <option key={c}>{c}</option>)}</select></Field>
      </div>
      {form.currency !== "TRY" && <Field label="Manuel kur (opsiyonel)"><input type="number" step="0.0001" value={form.manual_rate} onChange={e => setForm({ ...form, manual_rate: e.target.value })} /></Field>}
      <Field label="Açıklama"><textarea rows="2" data-testid="chk-desc" value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} /></Field>
      {err && <div className="auth-err">{String(err)}</div>}
      <div className="form-actions"><button type="button" className="outline-btn" onClick={onClose}>Vazgeç</button><button className="primary-btn" data-testid="chk-submit">Kaydet</button></div>
    </form>
  </Modal>;
}


// ---------- RATES PAGE ----------
function RatesPage() {
  const { user } = useAuth();
  const canEdit = user?.role !== "viewer";
  const [rates, setRates] = useState({ rates: [], updated_at: null });
  const [manual, setManual] = useState({ USD: "", EUR: "", GBP: "" });
  const [busy, setBusy] = useState(false);
  const load = () => axios.get(`${API}/fx/rates`).then(r => setRates(r.data));
  useEffect(() => { load(); }, []);
  const refresh = async () => { setBusy(true); try { await axios.post(`${API}/fx/refresh`); await load(); } finally { setBusy(false); } };
  const setMan = async (cur) => {
    const v = parseFloat(manual[cur]); if (!v) return;
    await axios.post(`${API}/fx/manual`, { currency: cur, rate: v });
    setManual({ ...manual, [cur]: "" }); load();
  };
  const clearMan = async (cur) => { await axios.delete(`${API}/fx/manual/${cur}`); load(); };
  return <div className="page">
    <section className="page-toolbar">
      <div><p className="eyebrow">TCMB · DÖVİZ</p><h2>Güncel kurlar</h2><p className="muted">{rates.updated_at ? `Son güncelleme: ${new Date(rates.updated_at).toLocaleString("tr-TR")}` : "Veri yok"}</p></div>
      {canEdit && <button className="outline-btn" onClick={refresh} disabled={busy} data-testid="refresh-rates"><RefreshCw size={16} /> Şimdi güncelle</button>}
    </section>
    <div className="rates-grid">
      {rates.rates.map(r => <div className="rate-card" key={r.currency} data-testid={`rate-${r.currency}`}>
        <div className="rate-head"><strong>{r.currency}/TRY</strong><span>{r.rate_date}</span></div>
        <div className="rate-nums">
          <div><span>Alış</span><strong>{r.buying.toFixed(4)}</strong></div>
          <div><span>Satış</span><strong>{r.selling.toFixed(4)}</strong></div>
        </div>
        {r.manual_rate && <div className="manual-badge" data-testid={`manual-${r.currency}`}>Manuel: {r.manual_rate.toFixed(4)} <button onClick={() => clearMan(r.currency)}><X size={12} /></button></div>}
        {canEdit && <div className="manual-set">
          <input data-testid={`manual-input-${r.currency}`} type="number" step="0.0001" placeholder="Manuel kur" value={manual[r.currency]} onChange={e => setManual({ ...manual, [r.currency]: e.target.value })} />
          <button className="link-btn" data-testid={`manual-save-${r.currency}`} onClick={() => setMan(r.currency)}>Uygula</button>
        </div>}
      </div>)}
    </div>
    <p className="muted" style={{ marginTop: 18 }}>Kurlar saatlik olarak TCMB resmi beslemesinden otomatik çekilir. Manuel kur yalnızca o günü etkiler.</p>
  </div>;
}

// ---------- CREDITS BY BANK PAGE ----------
function CreditsPage({ company, companies }) {
  const [groups, setGroups] = useState([]);
  const [meta, setMeta] = useState({ banks: [] });
  const [openBanks, setOpenBanks] = useState({});
  const [openPlans, setOpenPlans] = useState({});
  const [earlyClosure, setEarlyClosure] = useState(null);
  const [showLoanForm, setShowLoanForm] = useState(false);
  const { user } = useAuth();
  const canEdit = user?.role !== "viewer";
  useEffect(() => { axios.get(`${API}/meta`).then(r => setMeta(r.data)); }, []);
  const load = () => axios.get(`${API}/credits/by-bank`, { params: { company_id: company } }).then(r => setGroups(r.data));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [company]);
  const markPaid = async (id) => { await axios.post(`${API}/obligations/${id}/pay`); load(); };
  useEffect(() => {
    if (groups.length && Object.keys(openBanks).length === 0) {
      const o = {}; groups.forEach(b => { o[b.bank] = true; }); setOpenBanks(o);
    }
  }, [groups]); // eslint-disable-line
  const totalRemaining = groups.reduce((a, b) => a + b.remaining_amount_try, 0);
  const totalPaid = groups.reduce((a, b) => a + b.paid_amount_try, 0);
  const grandTotal = totalRemaining + totalPaid;
  return <div className="page">
    <section className="page-toolbar">
      <div><p className="eyebrow">KREDİLER</p><h2>Banka bazlı kredi ve plan takibi</h2>
        <p className="muted">{groups.length} banka · {groups.reduce((a, b) => a + b.total_plans, 0)} aktif plan · tüm tutarlar TL eşdeğeri</p></div>
      {canEdit && <div className="toolbar-actions"><button className="primary-btn" data-testid="add-loan-btn" onClick={() => setShowLoanForm(true)}><Plus size={17} /> Yeni kredi</button></div>}
    </section>

    <div className="credits-summary">
      <div className="panel summary-card">
        <span>Toplam borç</span>
        <strong>{money(grandTotal)}</strong>
        <div className="mini-bar"><i style={{ width: grandTotal ? `${(totalPaid / grandTotal) * 100}%` : "0%" }} /></div>
        <small>{money(totalPaid)} ödendi · {money(totalRemaining)} kaldı</small>
      </div>
      <div className="panel summary-card">
        <span>Toplam faiz</span>
        <strong className="interest-text">{money(groups.reduce((a, b) => a + b.total_interest, 0))}</strong>
        <small>Tüm bankalar toplam</small>
      </div>
      <div className="panel summary-card">
        <span>Gecikmiş taksit</span>
        <strong className="late-text">{groups.reduce((a, b) => a + b.overdue_count, 0)}</strong>
        <small>{groups.filter(b => b.overdue_count > 0).length} bankada</small>
      </div>
    </div>

    {groups.length === 0 && <div className="panel empty">Henüz kredi/taksit planı yok. "Yeni kredi" ile ekleyin.</div>}

    {groups.map(b => {
      const bankOpen = openBanks[b.bank] !== false;
      return <section className="panel bank-panel" key={b.bank} data-testid={`bank-group-${b.bank}`}>
        <button type="button" className="bank-head" onClick={() => setOpenBanks({ ...openBanks, [b.bank]: !bankOpen })} data-testid={`bank-toggle-${b.bank}`}>
          <div className="bank-head-left">
            <BankLogo name={b.bank} banks={meta.banks} size={38} />
            <div>
              <strong>{b.bank}</strong>
              <span>{b.total_plans} plan · {b.overdue_count > 0 && <b className="late-text">{b.overdue_count} gecikmiş · </b>}%{b.progress_pct} ödendi</span>
            </div>
          </div>
          <div className="bank-head-stats">
            <div><span>Ödenen</span><b className="income">{money(b.paid_amount_try)}</b></div>
            <div><span>Kalan</span><b>{money(b.remaining_amount_try)}</b></div>
            <div><span>Toplam faiz</span><b className="interest-text">{money(b.total_interest)}</b></div>
          </div>
          <ChevronDown size={20} className={`chev ${bankOpen ? "open" : ""}`} />
        </button>
        <div className="bank-progress"><i style={{ width: `${b.progress_pct}%` }} /></div>
        {bankOpen && <div className="bank-plans">
          {b.plans.map(p => {
            const c = companies.find(cc => cc.id === p.company_id);
            const pOpen = openPlans[p.plan_id];
            return <div className={`plan-card ${p.overdue_count > 0 ? "has-overdue" : ""}`} key={p.plan_id} data-testid={`credit-plan-${p.plan_id}`}>
              <button type="button" className="plan-head" onClick={() => setOpenPlans({ ...openPlans, [p.plan_id]: !pOpen })}>
                <div className="plan-left">
                  <div className="plan-ico"><CreditCard size={18} /></div>
                  <div className="plan-info">
                    <strong>{p.title}</strong>
                    <span><span className="company-pill" style={{ background: `${c?.color}22`, color: c?.color }}>{c?.short}</span> · {p.counterparty}</span>
                  </div>
                </div>
                <div className="plan-progress-wrap">
                  <div className="plan-progress-top">
                    <span><b>{p.paid_count}</b>/{p.total_count} ödendi</span>
                    <span className="plan-amount">{money(p.paid_try)} / <b>{money(p.total_try)}</b></span>
                  </div>
                  <div className="plan-progress-bar"><i style={{ width: `${p.progress_pct}%` }} /></div>
                  <small>
                    {p.overdue_count > 0 && <span className="late-text">● {p.overdue_count} gecikmiş · </span>}
                    {p.next_due && <span>Sonraki vade: {fmtDate(p.next_due)}</span>}
                    {p.pdf_attachment_id && <a className="attach-btn-inline" href={`${API}/attachments/${p.pdf_attachment_id}`} target="_blank" rel="noreferrer" onClick={ev => ev.stopPropagation()}><Paperclip size={11} /> PDF</a>}
                  </small>
                </div>
                <ChevronDown size={16} className={`chev ${pOpen ? "open" : ""}`} />
              </button>
              {canEdit && p.paid_count < p.total_count && (
                <div className="plan-actions">
                  <button type="button" className="link-btn early-btn" onClick={() => setEarlyClosure(p)} data-testid={`credit-early-${p.plan_id}`}>
                    <Zap size={13} /> Erken kapat
                  </button>
                </div>
              )}
              {pOpen && <div className="plan-items">
                <div className="table-scroll"><table className="cal-table">
                  <thead><tr><th>#</th><th>VADE</th><th>TUTAR</th><th>ANAPARA</th><th>FAİZ</th><th>KOMİSYON</th><th>DURUM</th><th></th></tr></thead>
                  <tbody>{p.items.map(it => <tr key={it.id}>
                    <td>{it.installment_index}/{it.installment_total}</td>
                    <td>{it.due_date}{it.status === "Gecikmiş" && <small className="late-text"> · {it.overdue_days}g</small>}</td>
                    <td><strong>{money(it.amount, it.currency)}</strong></td>
                    <td>{it.principal != null ? money(it.principal, it.currency) : "—"}</td>
                    <td className="interest-text">{it.interest != null ? money(it.interest, it.currency) : "—"}</td>
                    <td className="interest-text">{it.commission != null ? money(it.commission, it.currency) : "—"}</td>
                    <td><span className={`status-pill ${it.status === "Ödendi" ? "done" : it.status === "Gecikmiş" ? "late" : ""}`}>{it.status}</span></td>
                    <td>{canEdit && it.status !== "Ödendi" && <button className="link-btn pay-btn" onClick={() => markPaid(it.id)}><Check size={13} /> Ödendi</button>}</td>
                  </tr>)}</tbody>
                </table></div>
              </div>}
            </div>;
          })}
        </div>}
      </section>;
    })}
    {earlyClosure && <EarlyClosureModal plan={earlyClosure} onClose={() => setEarlyClosure(null)} onDone={() => { setEarlyClosure(null); load(); }} />}
    {showLoanForm && <LoanForm company={company} companies={companies} meta={meta} onClose={() => setShowLoanForm(false)} onDone={() => { setShowLoanForm(false); load(); }} />}
  </div>;
}

// ---------- LOAN FORM (phased: party_type → person → loan details) ----------
function LoanForm({ company, companies, meta, onClose, onDone }) {
  const [step, setStep] = useState(1);
  const [persons, setPersons] = useState([]);
  const [form, setForm] = useState({
    company_id: (company === "all" ? (companies[0]?.id || "") : company),
    party_type: "corporate",
    person_id: "",
    loan_kind: "Taksitli kredi",
    bank: "",
    principal: "",
    installment_amount: "",
    currency: "TRY",
    manual_rate: "",
    installment_count: 12,
    frequency: "monthly",
    total_interest: "",
    bsmv: "",
    other_costs: "",
    start_date: new Date().toISOString().slice(0, 10),
    first_payment_date: new Date(Date.now() + 30 * 86400000).toISOString().slice(0, 10),
  });
  const [pdfParsing, setPdfParsing] = useState(false);
  const [pdfRows, setPdfRows] = useState(null);
  const [attachId, setAttachId] = useState(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { axios.get(`${API}/persons`, { params: { type: "individual" } }).then(r => setPersons(r.data)); }, []);
  const handlePdf = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    setPdfParsing(true); setErr("");
    try {
      const fd = new FormData(); fd.append("file", file);
      const r = await axios.post(`${API}/obligations/installments/parse-pdf`, fd);
      setPdfRows(r.data.rows); setAttachId(r.data.attachment_id);
    } catch (ex) { setErr(ex.response?.data?.detail || "PDF okunamadı"); }
    finally { setPdfParsing(false); }
  };
  const submit = async (e) => {
    e.preventDefault(); setBusy(true); setErr("");
    try {
      const payload = {
        company_id: form.company_id, party_type: form.party_type,
        person_id: form.party_type === "corporate" ? form.company_id : form.person_id,
        loan_kind: form.loan_kind, bank: form.bank,
        principal: parseFloat(form.principal || 0),
        installment_amount: parseFloat(form.installment_amount || 0),
        currency: form.currency,
        installment_count: parseInt(form.installment_count),
        frequency: form.frequency,
        total_interest: parseFloat(form.total_interest || 0),
        bsmv: parseFloat(form.bsmv || 0),
        other_costs: parseFloat(form.other_costs || 0),
        start_date: form.start_date,
        first_payment_date: form.first_payment_date,
      };
      if (form.manual_rate) payload.manual_rate = parseFloat(form.manual_rate);
      if (attachId) payload.pdf_attachment_id = attachId;
      if (pdfRows && pdfRows.length === parseInt(form.installment_count)) payload.rows = pdfRows;
      await axios.post(`${API}/loans`, payload);
      onDone();
    } catch (ex) { setErr(ex.response?.data?.detail || "Kredi oluşturulamadı"); }
    finally { setBusy(false); }
  };
  const individuals = persons.filter(p => p.type === "individual" && p.status === "approved");
  return <Modal onClose={onClose} title="Yeni kredi" wide>
    <div className="step-tabs">
      <div className={`step ${step >= 1 ? "active" : ""}`} data-testid="loan-step-1">1 · Taraf</div>
      <div className={`step ${step >= 2 ? "active" : ""}`} data-testid="loan-step-2">2 · Kredi detayları</div>
      <div className={`step ${step >= 3 ? "active" : ""}`} data-testid="loan-step-3">3 · Taksit planı</div>
    </div>
    <form onSubmit={submit} className="form">
      {step === 1 && <>
        <Field label="Şirket (operasyonel)">
          <select data-testid="loan-company" value={form.company_id} onChange={e => setForm({ ...form, company_id: e.target.value })}>
            {companies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </Field>
        <Field label="Krediyi kim kullandı?">
          <div className="radio-row">
            <label className={`radio-chip ${form.party_type === "corporate" ? "active" : ""}`} data-testid="party-corporate">
              <input type="radio" value="corporate" checked={form.party_type === "corporate"} onChange={() => setForm({ ...form, party_type: "corporate", person_id: "" })} />
              Tüzel kişi (şirket)
            </label>
            <label className={`radio-chip ${form.party_type === "individual" ? "active" : ""}`} data-testid="party-individual">
              <input type="radio" value="individual" checked={form.party_type === "individual"} onChange={() => setForm({ ...form, party_type: "individual", person_id: individuals[0]?.id || "" })} />
              Şahıs
            </label>
          </div>
        </Field>
        {form.party_type === "individual" && (
          <Field label="Şahıs seç">
            <select data-testid="loan-person" value={form.person_id} onChange={e => setForm({ ...form, person_id: e.target.value })} required>
              <option value="">Şahıs seç…</option>
              {individuals.map(p => <option key={p.id} value={p.id}>{p.name}{p.role ? ` · ${p.role}` : ""}</option>)}
            </select>
          </Field>
        )}
        <div className="form-actions">
          <button type="button" className="outline-btn" onClick={onClose}>Vazgeç</button>
          <button type="button" className="primary-btn" onClick={() => setStep(2)} disabled={form.party_type === "individual" && !form.person_id} data-testid="loan-next-2">Devam →</button>
        </div>
      </>}

      {step === 2 && <>
        <div className="row2">
          <Field label="Kredi tipi">
            <select data-testid="loan-kind" value={form.loan_kind} onChange={e => setForm({ ...form, loan_kind: e.target.value })}>
              {(meta.loan_kinds || []).map(k => <option key={k}>{k}</option>)}
            </select>
          </Field>
          <Field label="Banka">
            <div className="bank-select-wrap">
              {form.bank && <BankLogo name={form.bank} banks={meta.banks} size={22} />}
              <select data-testid="loan-bank" value={form.bank} onChange={e => setForm({ ...form, bank: e.target.value })} required>
                <option value="">Banka seç…</option>
                {(meta.banks || []).map(b => <option key={b.id} value={b.name}>{b.name}</option>)}
              </select>
            </div>
          </Field>
        </div>
        <div className="row2">
          <Field label="Çekilen kredi tutarı"><input data-testid="loan-principal" type="number" step="0.01" value={form.principal} onChange={e => setForm({ ...form, principal: e.target.value })} required /></Field>
          <Field label="Para birimi"><select data-testid="loan-currency" value={form.currency} onChange={e => setForm({ ...form, currency: e.target.value })}>{["TRY", "USD", "EUR", "GBP"].map(c => <option key={c}>{c}</option>)}</select></Field>
        </div>
        <div className="row2">
          <Field label="Toplam taksit sayısı">
            <select data-testid="loan-count" value={form.installment_count} onChange={e => setForm({ ...form, installment_count: parseInt(e.target.value) })}>
              {(meta.installment_counts || [12]).map(n => <option key={n} value={n}>{n} ay</option>)}
            </select>
          </Field>
          <Field label="Ödeme sıklığı">
            <select data-testid="loan-frequency" value={form.frequency} onChange={e => setForm({ ...form, frequency: e.target.value })}>
              {Object.entries(meta.frequencies || { monthly: "Aylık" }).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
          </Field>
        </div>
        <Field label="Aylık taksit tutarı (PDF'de farklı taksit varsa otomatik tespit edilir)">
          <input data-testid="loan-installment" type="number" step="0.01" value={form.installment_amount} onChange={e => setForm({ ...form, installment_amount: e.target.value })} required />
        </Field>
        <div className="row3">
          <Field label="Toplam faiz"><input data-testid="loan-interest" type="number" step="0.01" value={form.total_interest} onChange={e => setForm({ ...form, total_interest: e.target.value })} /></Field>
          <Field label="BSMV"><input data-testid="loan-bsmv" type="number" step="0.01" value={form.bsmv} onChange={e => setForm({ ...form, bsmv: e.target.value })} /></Field>
          <Field label="Diğer masraflar"><input data-testid="loan-other" type="number" step="0.01" value={form.other_costs} onChange={e => setForm({ ...form, other_costs: e.target.value })} /></Field>
        </div>
        <div className="row2">
          <Field label="Kredi başlangıç"><input data-testid="loan-start" type="date" value={form.start_date} onChange={e => setForm({ ...form, start_date: e.target.value })} required /></Field>
          <Field label="İlk ödeme tarihi"><input data-testid="loan-first" type="date" value={form.first_payment_date} onChange={e => setForm({ ...form, first_payment_date: e.target.value })} required /></Field>
        </div>
        {form.currency !== "TRY" && <Field label="Manuel kur (opsiyonel)"><input data-testid="loan-manual-rate" type="number" step="0.0001" value={form.manual_rate} onChange={e => setForm({ ...form, manual_rate: e.target.value })} /></Field>}
        <div className="form-actions">
          <button type="button" className="outline-btn" onClick={() => setStep(1)}>← Geri</button>
          <button type="button" className="primary-btn" onClick={() => setStep(3)} disabled={!form.bank || !form.principal || !form.installment_amount} data-testid="loan-next-3">Devam →</button>
        </div>
      </>}

      {step === 3 && <>
        <div className="pdf-drop">
          <label className="outline-btn pdf-upload-btn" data-testid="loan-pdf-btn">
            {pdfParsing ? <><Loader2 size={16} className="spin" /> AI PDF okuyor…</> : attachId ? <><Check size={16} /> PDF yüklendi ({pdfRows?.length || 0} satır)</> : <><FileUp size={16} /> Banka ödeme planı PDF (opsiyonel)</>}
            <input type="file" accept="application/pdf,.pdf" hidden onChange={handlePdf} disabled={pdfParsing} />
          </label>
          <small>PDF yüklerseniz satırlar PDF'den alınır (farklı taksit tutarları dahil). Yüklemezseniz otomatik eşit dağıtım yapılır.</small>
        </div>
        {pdfRows && <div className="pdf-review" data-testid="loan-pdf-review">
          <strong>Önizleme: {pdfRows.length} taksit</strong>
          <div className="pdf-table-scroll"><table className="pdf-table">
            <thead><tr><th>#</th><th>Vade</th><th>Taksit</th><th>Anapara</th><th>Faiz</th><th>Komisyon</th></tr></thead>
            <tbody>{pdfRows.map((r, i) => <tr key={i}>
              <td>{i + 1}</td>
              <td><input type="date" value={r.due_date || ""} onChange={e => { const n = [...pdfRows]; n[i] = { ...n[i], due_date: e.target.value }; setPdfRows(n); }} /></td>
              <td><input type="number" value={r.amount ?? ""} onChange={e => { const n = [...pdfRows]; n[i] = { ...n[i], amount: e.target.value }; setPdfRows(n); }} /></td>
              <td><input type="number" value={r.principal ?? ""} onChange={e => { const n = [...pdfRows]; n[i] = { ...n[i], principal: e.target.value }; setPdfRows(n); }} /></td>
              <td><input type="number" value={r.interest ?? ""} onChange={e => { const n = [...pdfRows]; n[i] = { ...n[i], interest: e.target.value }; setPdfRows(n); }} /></td>
              <td><input type="number" value={r.commission ?? ""} onChange={e => { const n = [...pdfRows]; n[i] = { ...n[i], commission: e.target.value }; setPdfRows(n); }} /></td>
            </tr>)}</tbody>
          </table></div>
        </div>}
        {err && <div className="auth-err">{String(err)}</div>}
        <div className="form-actions">
          <button type="button" className="outline-btn" onClick={() => setStep(2)}>← Geri</button>
          <button type="submit" className="primary-btn" disabled={busy} data-testid="loan-submit">{busy ? "Oluşturuluyor…" : `${form.installment_count} taksitlik kredi oluştur`}</button>
        </div>
      </>}
    </form>
  </Modal>;
}

// ---------- FINANCE COSTS PAGE ----------
function FinanceCostsPage({ company, companies }) {
  const [data, setData] = useState(null);
  const [monthly, setMonthly] = useState([]);
  const [meta, setMeta] = useState({ banks: [] });
  useEffect(() => { axios.get(`${API}/meta`).then(r => setMeta(r.data)); }, []);
  const load = () => {
    axios.get(`${API}/finance-costs`, { params: { company_id: company } }).then(r => setData(r.data));
    axios.get(`${API}/finance-costs/monthly`, { params: { company_id: company } }).then(r => setMonthly(r.data.months));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [company]);
  if (!data) return <div className="page"><p className="muted">Yükleniyor…</p></div>;
  const s = data.summary;
  const criticalMonths = monthly.filter(m => m.critical);
  const monthLabel = (m) => { const [y, mo] = m.split("-"); return new Date(y, parseInt(mo) - 1, 1).toLocaleDateString("tr-TR", { month: "short", year: "2-digit" }); };
  const chartData = monthly.map(m => ({ ...m, label: monthLabel(m.month) }));
  return <div className="page">
    <section className="welcome">
      <div><p className="eyebrow">FİNANS GİDERLERİ</p><h2>Ödenen maliyetler ve farklar</h2>
        <p className="muted">{s.paid_count} ödenmiş taksit · {s.escrow_returned_count} iade edilmiş emanet · tüm tutarlar TL eşdeğeri</p></div>
    </section>

    <section className="metric-grid finance-metrics">
      <Metric label="Toplam faiz & ek maliyet" value={money(s.total_interest)} change="faiz + fark" trend="warn" icon="%" />
      <Metric label="Toplam komisyon" value={money(s.total_commission)} change="KKDF+BSMV+komisyon" trend="warn" icon="K" />
      <Metric label="Kur farkı (manuel vs TCMB)" value={money(s.total_fx_diff)} change={s.total_fx_diff >= 0 ? "lehte" : "aleyhte"} trend={s.total_fx_diff >= 0 ? "up" : "down"} icon="₺" />
      <Metric label="Emanet iade farkı" value={money(s.total_escrow_diff)} change={`${s.escrow_returned_count} iade`} trend={s.total_escrow_diff >= 0 ? "up" : "down"} icon="≈" />
    </section>

    <div className="grand-total panel" data-testid="grand-total">
      <div><p className="eyebrow">TOPLAM FİNANS GİDERİ</p>
        <strong>{money(s.grand_total_cost)}</strong>
        <small className="muted">Faiz + komisyon + kur farkı + emanet iade farkı toplamı</small>
      </div>
      <div className="totals-split">
        <div><span>Anapara</span><strong>{money(s.total_principal)}</strong></div>
        <div><span>Faiz</span><strong className="interest-text">{money(s.total_interest)}</strong></div>
        <div><span>Komisyon</span><strong className="interest-text">{money(s.total_commission)}</strong></div>
      </div>
    </div>

    {chartData.length > 0 && (
      <section className="panel chart-panel" data-testid="finance-cost-chart">
        <div className="panel-heading">
          <div><h3>Aylık trend</h3><p>Faiz, komisyon, kur farkı · {criticalMonths.length > 0 && <span className="late-text">kritik ay vurgulanmıştır</span>}</p></div>
          <div className="chart-legend">
            <span><i style={{ background: "#f1b956" }} /> Faiz</span>
            <span><i style={{ background: "#a970ff" }} /> Komisyon</span>
            <span><i style={{ background: "#4f91ff" }} /> Kur farkı</span>
          </div>
        </div>
        <div style={{ height: 300, padding: "20px 20px 10px" }}>
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={chartData} margin={{ top: 10, right: 20, left: 10, bottom: 5 }}>
              <CartesianGrid stroke="#22344a" strokeDasharray="3 3" />
              <XAxis dataKey="label" tick={{ fill: "#8b98aa", fontSize: 11 }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fill: "#8b98aa", fontSize: 11 }} axisLine={false} tickLine={false}
                tickFormatter={v => Intl.NumberFormat("tr-TR", { notation: "compact" }).format(v)} />
              <Tooltip
                contentStyle={{ background: "#172131", border: "1px solid #34445b", borderRadius: 8, color: "#fff", fontSize: 11 }}
                formatter={(v) => money(v)}
                labelFormatter={(_, p) => p?.[0]?.payload?.critical ? `${_} · KRİTİK` : _}
              />
              <Line type="monotone" dataKey="interest" stroke="#f1b956" strokeWidth={2.5} dot={{ r: 3 }} activeDot={{ r: 5 }} name="Faiz" />
              <Line type="monotone" dataKey="commission" stroke="#a970ff" strokeWidth={2.5} dot={{ r: 3 }} activeDot={{ r: 5 }} name="Komisyon" />
              <Line type="monotone" dataKey="fx_diff" stroke="#4f91ff" strokeWidth={2.5} dot={{ r: 3 }} activeDot={{ r: 5 }} name="Kur farkı" />
              {criticalMonths.map(cm => <ReferenceDot key={cm.month} x={monthLabel(cm.month)} y={cm.total_cost} r={9} fill="#f06c6c" stroke="#fff" strokeWidth={1.5} />)}
            </LineChart>
          </ResponsiveContainer>
        </div>
        {criticalMonths.length > 0 && <div className="critical-list">
          {criticalMonths.map(cm => <div key={cm.month} className="critical-item" data-testid={`critical-${cm.month}`}>
            <AlertTriangle size={14} />
            <strong>{monthLabel(cm.month)}</strong>
            <span>Toplam {money(cm.total_cost)}</span>
          </div>)}
        </div>}
      </section>
    )}

    <section className="panel" style={{ marginTop: 14 }}>
      <div className="panel-heading"><div><h3>Ödenen taksitler</h3><p>Anapara, faiz, komisyon ve ekli PDF</p></div></div>
      {data.obligations.length === 0 ? <div className="empty">Henüz ödenmiş taksit yok. Takvimden "Ödendi" butonuyla işaretleyin.</div> :
        <div className="table-scroll"><table>
          <thead><tr><th>VADE</th><th>ŞİRKET</th><th>BAŞLIK</th><th>BANKA</th><th>TUTAR</th><th>ANAPARA</th><th>FAİZ</th><th>KOMİSYON</th><th>KUR FARKI</th><th>PDF</th></tr></thead>
          <tbody>{data.obligations.map(o => {
            const c = companies.find(cc => cc.id === o.company_id);
            return <tr key={o.id} data-testid={`fc-row-${o.id}`}>
              <td>{o.due_date}</td>
              <td><span className="company-pill" style={{ background: `${c?.color}22`, color: c?.color }}>{c?.short}</span></td>
              <td><strong>{o.title}</strong><span>{o.counterparty}</span></td>
              <td><BankCell name={o.bank} banks={meta.banks} /></td>
              <td>{money(o.amount, o.currency)}</td>
              <td>{o.principal != null ? money(o.principal, o.currency) : "—"}</td>
              <td className="interest-text">{o.interest != null ? money(o.interest, o.currency) : (o.extra_cost > 0 ? money(o.extra_cost, o.currency) : "—")}</td>
              <td className="interest-text">{o.commission != null ? money(o.commission, o.currency) : "—"}</td>
              <td className={o.fx_diff_try >= 0 ? "income" : "expense"}>{o.fx_diff_try !== 0 ? money(o.fx_diff_try) : "—"}</td>
              <td>{o.pdf_attachment_id ? <a className="link-btn attach-btn" href={`${API}/attachments/${o.pdf_attachment_id}`} target="_blank" rel="noreferrer"><Paperclip size={13} /></a> : "—"}</td>
            </tr>;
          })}</tbody>
        </table></div>
      }
    </section>

    <section className="panel" style={{ marginTop: 14 }}>
      <div className="panel-heading"><div><h3>İade edilen emanetler</h3><p>Alış ve iade tutar farkları</p></div></div>
      {data.escrows.length === 0 ? <div className="empty">İade edilen emanet yok.</div> :
        <div className="table-scroll"><table>
          <thead><tr><th>BAŞLIK</th><th>ŞİRKET</th><th>KARŞI TARAF</th><th>ALIŞ</th><th>İADE</th><th>FARK</th><th>FARK (TL)</th></tr></thead>
          <tbody>{data.escrows.map(e => {
            const c = companies.find(cc => cc.id === e.company_id);
            return <tr key={e.id} data-testid={`fc-escrow-${e.id}`}>
              <td><strong>{e.title}</strong></td>
              <td><span className="company-pill" style={{ background: `${c?.color}22`, color: c?.color }}>{c?.short}</span></td>
              <td>{e.counterparty}</td>
              <td>{money(e.amount, e.currency)}</td>
              <td>{money(e.returned_amount, e.currency)}</td>
              <td className={e.diff >= 0 ? "income" : "expense"}>{money(e.diff, e.currency)}</td>
              <td className={e.diff_try >= 0 ? "income" : "expense"}>{money(e.diff_try)}</td>
            </tr>;
          })}</tbody>
        </table></div>
      }
    </section>
  </div>;
}
function Reports({ data }) {
  return <div className="page">
    <section className="page-toolbar">
      <div><p className="eyebrow">YÖNETİCİ ÖZETİ</p><h2>Finansal raporlar</h2><p className="muted">Tüm tutarlar TL eşdeğerindedir.</p></div>
      <button className="primary-btn" data-testid="download-report-button"><FileText size={16} /> Rapor oluştur</button>
    </section>
    <div className="report-cards">
      <div className="panel report-card"><span>Net nakit marjı</span><strong>{data.income > 0 ? `%${((data.net_cash / data.income) * 100).toFixed(1)}` : "—"}</strong><div className="mini-bar"><i style={{ width: `${Math.min(100, data.income > 0 ? (data.net_cash / data.income) * 100 : 0)}%` }} /></div><small>Geçen döneme göre</small></div>
      <div className="panel report-card"><span>Toplam gelir</span><strong>{money(data.income)}</strong><small className="income">↑ {data.transactions.filter(t => t.type === "income").length} kayıt</small></div>
      <div className="panel report-card"><span>Vadesi geçen</span><strong className="danger-text">{money(data.obligations.filter(o => o.status === "Gecikmiş").reduce((a, o) => a + o.try_amount, 0))}</strong><small className="danger-text">{data.obligations.filter(o => o.status === "Gecikmiş").length} gecikmiş</small></div>
    </div>
    <section className="panel report-table">
      <div className="panel-heading"><div><h3>Şirket karşılaştırması</h3><p>Tüm tutarlar TL eşdeğeri</p></div></div>
      <table><thead><tr><th>ŞİRKET</th><th>GELİR</th><th>GİDER</th><th>NET NAKİT</th><th>DURUM</th></tr></thead>
        <tbody>{data.companies.map(c => {
          const txs = data.transactions.filter(t => t.company_id === c.id);
          const inc = txs.filter(t => t.type === "income").reduce((a, t) => a + t.try_amount, 0);
          const exp = txs.filter(t => t.type === "expense").reduce((a, t) => a + t.try_amount, 0);
          const net = inc - exp;
          return <tr key={c.id}><td><b className="dot" style={{ background: c.color }} /> {c.name}</td>
            <td className="income">{money(inc)}</td><td>{money(exp)}</td><td><b>{money(net)}</b></td>
            <td><span className={net >= 0 ? "status-ok" : "late-text"}>{net >= 0 ? <><CheckCircle2 size={14} /> Sağlıklı</> : <><AlertTriangle size={14} /> Dikkat</>}</span></td>
          </tr>;
        })}</tbody>
      </table>
    </section>
  </div>;
}

// ---------- SHARED ----------
function Modal({ title, children, onClose, wide }) {
  return <div className="modal-backdrop" onClick={onClose} data-testid="modal-backdrop">
    <div className={`modal ${wide ? "modal-wide" : ""}`} onClick={e => e.stopPropagation()}>
      <div className="modal-head"><h3>{title}</h3><button onClick={onClose} data-testid="modal-close"><X size={18} /></button></div>
      {children}
    </div>
  </div>;
}
function Field({ label, children }) { return <label className="field"><span>{label}</span>{children}</label>; }

// ---------- APP ----------
function AppContent() {
  const { user } = useAuth();
  const [company, setCompany] = useState("all");
  const [companies, setCompanies] = useState([]);
  const [data, setData] = useState({ transactions: [], obligations: [], companies: [], cash_balance: 0, income: 0, expense: 0, due_amount: 0 });
  const loadDash = () => axios.get(`${API}/dashboard?company_id=${company}`).then(r => setData(r.data));
  useEffect(() => { axios.get(`${API}/companies`).then(r => setCompanies(r.data)); }, []);
  // Auto-select single allowed company for scoped non-admin users
  useEffect(() => {
    const allowed = user?.allowed_companies || [];
    if (user?.role !== "admin" && allowed.length === 1 && company === "all") {
      setCompany(allowed[0]);
    }
  }, [user, company]);
  useEffect(() => { loadDash(); /* eslint-disable-next-line */ }, [company]);
  return <Shell company={company} setCompany={setCompany} companies={companies}>
    <Routes>
      <Route path="/" element={<Dashboard data={data} company={company} />} />
      <Route path="/transactions" element={<TransactionsPage company={company} data={data} reload={loadDash} />} />
      <Route path="/calendar" element={<CalendarPage company={company} companies={companies} />} />
      <Route path="/cari" element={<CariPage company={company} companies={companies} user={user} />} />
      <Route path="/bank-accounts" element={<BankAccountsPage company={company} companies={companies} user={user} />} />
      <Route path="/operations" element={<OperationsPage company={company} companies={companies} user={user} />} />
      <Route path="/checks" element={<ChecksPage company={company} companies={companies} />} />
      <Route path="/escrows" element={<EscrowsPage company={company} companies={companies} />} />
      <Route path="/credits" element={<CreditsPage company={company} companies={companies} />} />
      <Route path="/credit-cards" element={<CreditCardsPage company={company} companies={companies} user={user} />} />
      <Route path="/daily-expenses" element={<DailyExpensesPage company={company} companies={companies} user={user} />} />
      <Route path="/finance-costs" element={<FinanceCostsPage company={company} companies={companies} />} />
      <Route path="/rates" element={<RatesPage />} />
      <Route path="/reports" element={<Reports data={data} />} />
      <Route path="/users" element={<UsersPage />} />
      <Route path="/companies" element={<CompaniesPage />} />
      <Route path="/audit-log" element={<AuditLogPage />} />
      <Route path="*" element={<Dashboard data={data} company={company} />} />
    </Routes>
  </Shell>;
}

function AppRouter() {
  // Detect OAuth callback FIRST, synchronously (not in useEffect) to avoid race conditions.
  // IMPORTANT: use useLocation().hash, NOT window.location.hash (not reactive).
  const location = useLocation();
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/pending" element={<PendingScreen />} />
      <Route path="/*" element={<ProtectedRoute><AppContent /></ProtectedRoute>} />
    </Routes>
  );
}

export default function App() {
  return <BrowserRouter>
    <AuthProvider>
      <AppRouter />
    </AuthProvider>
  </BrowserRouter>;
}
